export const reelsCreator = {
  reelsCreatorHomePage: {
    title: 'Reels Creator',
    brandKits: 'Brand kits',
    settings: 'Settings',
  },
  reelsCreatorBrandsPage: {
    title: 'Brand kits',
  },
  reelsCreatorSettingsPage: {
    title: 'Settings',
  },
  reelSettings: {
    intro:
      'These settings apply to every workspace that uses Reels Creator. Only Monks.Flow admins can see and change them, and each one saves as soon as you change it.',
    isDefault: 'Default value',
    changedBy: 'Changed by {{name}} on {{date}}',
    loadFailed: 'The settings could not be loaded.',
    retry: 'Try again',
    saveFailed: 'This change could not be saved. Try again.',
    provider: {
      title: 'Video provider',
      description:
        "Makes every reel's video. ByteDance and Runway run the same SeedDance 2 model; Wan 3 and Hailuo 3 are other models on the same Runway account, to compare against it. A video already being made keeps the provider it started with.",
      noKey: 'This provider has no API key on the server, so reels cannot use it yet.',
      options: {
        seedance2: 'ByteDance (BytePlus)',
        runway: 'Runway',
        runway_wan3: 'Wan 3 (Runway)',
        runway_hailuo3: 'Hailuo 3 by MiniMax (Runway)',
      },
    },
    videoMode: {
      title: 'Video mode',
      description:
        "How every reel's video is made. Shot by shot pins each shot between its start and end frames and re-makes only the shots that changed; whole makes the reel in one request, and any change makes all of it again. A video already being made keeps the mode it started with.",
      options: {
        per_shot: 'Shot by shot',
        whole: 'Whole video',
      },
    },
    imageProvider: {
      title: 'Frame image model',
      description:
        'Draws the storyboard frames, start and end, and their edits. References, masters and inpainting stay on Gemini. A frame that needs more references than the model takes is refused, never trimmed.',
      noKey: 'This image model has no API key on the server, so reels cannot use it yet.',
      options: {
        gemini: 'Gemini',
        seedream: 'Seedream (ModelArk)',
        runway: 'Runway',
      },
    },
    faceCheck: {
      title: 'Face check',
      description:
        'Registers storyboard frames with ByteDance before a video is made, so faces in them are not refused as real people. ByteDance only.',
    },
    cutDowns: {
      title: 'Cut-down versions',
      description:
        'Shorter versions of a reel alongside the main one. Saved with each new reel; the cut-downs themselves are not built yet.',
    },
    multiAngle: {
      title: 'Extra camera angles by default',
      description:
        "Draws extra views of a shared location. Used for each brand's automatic Default template and for reels whose template was deleted; other templates set their own.",
    },
    coverFrame: {
      title: 'Cover frame',
      description:
        "Which storyboard frame becomes a reel's cover once it is drawn, unless someone uploads their own. The first frame is used while it is missing.",
      option: 'Frame {{frame}}',
    },
  },
  reelAdmin: {
    tabs: {
      label: 'Settings and diagnostics',
      settings: 'Settings',
      sessions: 'All reels',
      logs: 'Logs',
      frames: 'Frame diagnostics',
      videos: 'Video diagnostics',
    },
    retry: 'Try again',
    none: '—',
    version: 'v{{version}}',
    seconds: '{{value}} s',
    showPrompt: 'Show prompt',
    hidePrompt: 'Hide prompt',
    promptLength: 'Prompt ({{count}} characters)',
    details: 'Details',
    lookup: {
      label: 'Reel ID',
      placeholder: 'Full reel ID or its first 8 characters',
      load: 'Load',
      notFound: 'No reel in this workspace has that ID.',
      ambiguous: 'More than one reel starts with that ID. Paste more of it.',
      invalid: 'That is not a reel ID.',
      failed: 'The diagnostics could not be loaded.',
    },
    sessions: {
      intro:
        'Every reel in this workspace, most recently changed first. Copy an ID to look the reel up in the diagnostics.',
      search: 'Search by title',
      columns: {
        title: 'Title',
        status: 'Status',
        lastChanged: 'Last changed',
        id: 'Reel ID',
      },
      untitled: '(Untitled)',
      completed: 'Completed',
      inProgress: 'In progress',
      copyId: 'Copy reel ID {{id}}',
      copied: 'Copied',
      empty: 'No reels in this workspace yet.',
      noResults: 'No reel title matches your search.',
      loadFailed: 'The reels could not be loaded.',
      loadMore: 'Load more',
    },
    logs: {
      intro:
        "The latest server log lines from this workspace's requests and jobs, read every few seconds. Each server keeps its own lines, so a reload can show a different set.",
      pause: 'Pause',
      resume: 'Resume',
      levels: {
        label: 'Level',
        all: 'All',
        log: 'Log',
        warn: 'Warning',
        error: 'Error',
        debug: 'Debug',
      },
      search: 'Filter by text',
      autoScroll: 'Auto-scroll',
      count: '{{visible}} / {{total}}',
      paused: 'Paused. No new lines are being read.',
      waiting: 'Waiting for log lines…',
      noMatch: 'No lines match the filter.',
      loadFailed: 'The logs could not be read. Trying again in a few seconds.',
    },
    frames: {
      intro:
        'Every storyboard frame of a reel, with the prompt sent to the image model, the references it was drawn from, its plate and its drift check. Compare a frame that kept the character with one that drifted.',
      versionsFilter: 'Versions shown',
      filter: {
        active: 'Active only',
        all: 'All versions',
      },
      summary: '{{shown}} of {{total}} frames shown ({{scenes}} scenes)',
      scene: 'Scene {{number}}',
      sceneGone: 'Scene no longer in the script',
      versions_one: '{{count}} version',
      versions_other: '{{count}} versions',
      empty: 'This reel has no storyboard frames yet.',
      columns: {
        version: 'Version',
        image: 'Image',
        refs: 'References by type',
        characters: 'Characters',
        plate: 'Plate',
        origin: 'Made by',
        qc: 'Drift check',
        status: 'Status',
        prompt: 'Prompt',
      },
      refCounts:
        'char {{character}} · prod {{product}} · prop {{prop}} · loc {{location}} · extra {{extra}}',
      qcValue: '{{verdict}} {{score}}',
      openImage: 'Open version {{version}} full size',
      chipsUsed: 'References used',
      chipValue: '{{key}} v{{version}} {{type}}',
      instruction: 'Edit instruction',
      meta: 'Scene: {{sceneId}}\nCreated: {{createdAt}}\nPlate: {{plate}}',
      qcDetail: 'Drift check, in full',
    },
    videos: {
      intro:
        "Every video run of a reel, failed ones included: the prompt as sent, the provider's task ID to look up on its own dashboard, the frames sent, and where the cuts were found and how.",
      empty: 'No video has been made for this reel yet.',
      columns: {
        version: 'Version',
        status: 'Status',
        provider: 'Provider',
        taskId: 'Task ID',
        frames: 'Frames sent',
        length: 'Length',
        cuts: 'Cuts found by',
        started: 'Started',
        prompt: 'Prompt',
      },
      failedWith: 'failed: {{code}}',
      breakpoints: 'Cut points',
      breakpoint: '{{sceneId}}: {{start}} s to {{end}} s',
      meta: 'Cut confidence: {{confidence}}\nRequested length: {{requested}} s\nRatio: {{ratio}}, {{resolution}}\nSubmits sent: {{submits}}\nDeadline: {{deadlineAt}}\nLast updated: {{updatedAt}}',
      openVideo: 'Open the video',
    },
  },
  reelsSessionList: {
    reelSessionsToolbar: {
      cardsView: 'Cards view',
      listView: 'List view',
      status: 'Status',
      allStatuses: 'All',
      created: 'Created',
      anyTime: 'Any time',
      lastDays_one: 'Last {{count}} day',
      lastDays_other: 'Last {{count}} days',
      newReel: 'New reel',
    },
    reelSessionsEmptyState: {
      title: 'Create your first video',
      description:
        'With Reels Creator, your ideas go from brief to screen. Generate concepts with AI, shape each scene, refine your storyboard, and bring everything together in the editor.',
      create: 'Create',
      readOnlyTitle: 'No reels yet',
      readOnlyDescription:
        'Reels made in this workspace will show up here. Only members with write access can create them.',
    },
    reelSessionsNoResults: {
      title: 'Nothing found',
      description:
        'Try a different search term, check your spelling, or broaden your search to find what you’re looking for.',
      clearFilters: 'Clear filters',
    },
    reelSessionsError: {
      title: 'Could not load your reels',
      description: 'Something went wrong while loading your reels. Try again.',
      retry: 'Retry',
    },
    reelSessionList: {
      loadMore: 'Load more',
    },
    reelSessionStatusBadge: {
      completed: 'Completed',
      inProgress: 'In progress',
    },
    reelSessionCover: {
      defaultCover: '{{title}}: Monks.Flow default cover',
    },
    reelCoverMenu: {
      trigger: 'Cover of {{title}}',
      fileInput: 'Choose a cover image for {{title}}',
      upload: 'Upload a cover',
      automatic: 'Use automatic cover',
      uploadFailed: 'The cover image could not be uploaded. Try again.',
    },
    reelSessionCard: {
      delete: 'Delete {{title}}',
      createdBy: 'Created by {{name}}',
    },
    reelSessionRow: {
      chooseCampaign: 'Choose campaign',
    },
    reelSessionRowHeader: {
      reels: 'Reels',
      status: 'Status',
      created: 'Created',
      owner: 'Owner',
    },
    deleteReelSessionDialog: {
      title: 'Delete session?',
      body: 'Delete "{{title}}"? This cannot be undone.',
      cancel: 'Cancel',
      confirm: 'Delete',
      success: 'Reel deleted',
      failure: 'Could not delete the reel. Try again.',
    },
  },
  reelsHome: {
    navigator: {
      label: 'Brands and campaigns',
      open: 'Brands and campaigns',
      searchPlaceholder: 'Search brands, campaigns, reels',
      allReels: 'All reels',
      needsCampaign: 'Needs a campaign',
      brands: 'Brands',
      loadFailed: 'Could not load the brands.',
      retry: 'Retry',
      noMatches: 'No brand or campaign matches.',
      noBrands: 'No brands yet.',
      noCampaigns: 'No campaigns yet',
      addBrand: 'Add brand',
      showCampaigns: 'Show the campaigns of {{brand}}',
      hideCampaigns: 'Hide the campaigns of {{brand}}',
    },
    campaignCard: {
      reels_one: '{{count}} reel',
      reels_other: '{{count}} reels',
      completed_one: '{{count}} completed',
      completed_other: '{{count}} completed',
      due: 'Due {{date}}',
      folders_one: '{{count}} approval folder',
      folders_other: '{{count}} approval folders',
    },
    campaignHeader: {
      openProject: 'Open project',
    },
    campaignEmpty: {
      title: 'No reels in {{campaign}} yet',
      description:
        'A reel started here belongs to this campaign, and its approval folders are offered first when the video is saved.',
      newReelHere: 'New reel here',
    },
    brandBoard: {
      summary: '{{campaigns}} · {{reels}}',
      campaigns_one: '{{count}} campaign',
      campaigns_other: '{{count}} campaigns',
      brandKit: 'Brand kit',
      errorTitle: 'Could not load the campaigns',
      errorDescription: 'Something went wrong while loading this brand’s campaigns. Try again.',
      emptyTitle: 'No campaigns for {{brand}} yet',
      emptyDescription:
        'A campaign is a Monks.Flow project. Start a reel and create the first campaign as you go, or link a project the team already has.',
      linkProject: 'Link an existing project',
      newCampaign: 'New campaign',
      newCampaignHint:
        'Start a reel and create its campaign as you go, or link an existing project.',
    },
    unfiled: {
      title: 'Needs a campaign',
      banner_one:
        '{{count}} reel needs a campaign. It was made before reels belonged to campaigns. Choose one so it shows under its brand and saves to the right approval folders.',
      banner_other:
        '{{count}} reels need a campaign. They were made before reels belonged to campaigns. Choose one for each so they show under their brand and save to the right approval folders.',
      empty: 'Every reel has a campaign.',
    },
    all: {
      title: 'All reels',
      summary: '{{reels}} across {{brands}}',
      brands_one: '{{count}} brand',
      brands_other: '{{count}} brands',
    },
    campaignPicker: {
      brandStepTitle: 'Which brand is this reel for?',
      campaignStepTitle: 'Which campaign is it part of?',
      fileTitle: 'Choose a campaign for {{title}}',
      linkTitle: 'Link a project as a campaign',
      newReelDescription: 'Every reel belongs to a brand and a campaign.',
      campaignDescription:
        'The campaign’s approval folders are offered first when the finished video is saved.',
      steps: 'Steps',
      step: {
        brand: 'Brand',
        campaign: 'Campaign',
        brief: 'Brief',
      },
      campaign: 'Campaign',
      changeBrand: 'Change brand',
      linkProject: 'Link an existing project',
      linkProjectHint: 'A Monks.Flow project of type Campaigns that no brand has yet.',
      project: 'Project',
      chooseProject: 'Choose a project',
      searchProjects: 'Search projects',
      newCampaign: 'New campaign',
      newCampaignHint:
        'Creates a Monks.Flow project of type Campaigns in this workspace, linked to this brand.',
      newCampaignNotAllowed:
        'Only people who can create projects in this workspace can start a new campaign. Link an existing project, or ask a workspace admin.',
      campaignName: 'Campaign name',
      campaignNamePlaceholder: 'e.g. Winter push',
      back: 'Back',
      cancel: 'Cancel',
      next: 'Next',
      continueToBrief: 'Continue to brief',
      linkConfirm: 'Link project',
      fileConfirm: 'Save campaign',
      errors: {
        inOtherBrand: 'That project is already a campaign of another brand.',
        createForbidden:
          'You cannot create projects in this workspace. Link an existing project instead.',
        projectNotFound: 'That project could not be found, or you cannot see it.',
        generic: 'Something went wrong. Try again.',
      },
    },
  },
  reelBrief: {
    reelBriefFields: {
      templateBundle: {
        label: 'Included with this template',
        music: 'Music:',
        endTag: 'End tag:',
        overlayFolder: 'Overlays:',
        effectsOnly: 'Sound effects only',
      },
      title: 'Title',
      titlePlaceholder: 'e.g. Spring launch reel',
      brand: 'Brand',
      brandPlaceholder: 'Select a brand',
      template: 'Template',
      templatePlaceholder: 'Select a template',
      product: 'Product',
      productPlaceholder: 'Select a product',
      noActiveProducts: 'This brand has no active products yet. Add one in the brand kit.',
      duration: 'Duration',
      durationValue: '{{seconds}} seconds',
      aspectRatio: 'Aspect ratio',
      aspectRatioValue: '{{ratio}} ({{orientation}})',
      orientation: {
        vertical: 'Vertical',
        landscape: 'Landscape',
        square: 'Square',
        portrait: 'Portrait',
      },
      setByTemplate: 'Set by the template',
      conceptModel: 'Concept model',
      scriptModel: 'Script model',
      storyboardModel: 'Storyboard model',
      modelPlaceholder: 'Select a model',
      models: {
        haiku: 'Haiku (Fast)',
        sonnet: 'Sonnet (Recommended)',
        opus: 'Opus (Best quality)',
        gemini: 'Gemini Flash',
        geminiPro: 'Gemini Pro',
      },
      character: 'Character',
      characterPlaceholder: 'Select a character',
      videoProvider: 'Video model',
      videoProviders: {
        default: 'Workspace default',
        seedance2: 'SeedDance 2.0 (ModelArk)',
        runway: 'Runway',
        runway_wan3: 'Wan 3 (Runway)',
        runway_hailuo3: 'Hailuo 3 by MiniMax (Runway)',
      },
      videoMode: 'Video mode',
      videoModes: {
        default: 'Workspace default',
        per_shot: 'Shot by shot',
        whole: 'Whole video',
      },
      imageProvider: 'Frame image model',
      imageProviders: {
        default: 'Workspace default',
        gemini: 'Gemini',
        seedream: 'Seedream (ModelArk)',
        runway: 'Runway',
        'gpt-image-2.5-flare': 'GPT Image 2.5 Flare',
        'gpt-image-2': 'GPT Image 2',
      },
      ideaMode: 'Ideas',
      ideaModes: {
        explore: 'Explore new ideas',
        iterate: "Iterate on the brief's idea",
      },
      frameContinuity: 'Storyboard frames',
      frameContinuityModes: {
        chained: 'Each shot follows on from the one before',
        together: 'All at once (faster)',
      },
      templatesError: 'Could not load the templates.',
      productsError: 'Could not load the products.',
      retry: 'Retry',
      productNotInBrand: 'This product is no longer available for this brand. Pick another one.',
      templateNotFound: 'This template is no longer available. Pick another one.',
    },
    briefingField: {
      briefing: 'Briefing',
      placeholder: 'Describe what you want to achieve and share what you know.',
      useProjectBrief: 'Use a project brief',
      useCampaignBrief: 'Fill the brief from the {{campaign}} project brief',
      direction: 'Direction',
      directionPlaceholder:
        'Add direction on top of the brief (optional). It wins where the two disagree.',
      uploadBriefFile: 'Upload a brief file',
      briefFileInput: 'Brief file',
      attachedFiles: 'Attached brief files',
      removeFile: 'Remove {{name}}',
      fileUnreadable:
        'Could not read {{name}}. Upload a PDF, Word, Markdown or text file that has text in it.',
      fileTooLong:
        '{{name}} would make the brief too long to send. Remove a file or use a shorter one.',
    },
    projectBriefPicker: {
      brief: 'Brief',
      selectBrief: 'Select a brief',
      searchBriefs: 'Search briefs',
      untitledBrief: 'Untitled brief',
      writeInstead: 'Write the brief here instead',
      removeProjectBrief: 'Remove the project brief',
    },
    reelBriefFooter: {
      back: 'Back',
      next: 'Next',
      creating: 'Creating',
    },
    reelBriefStatus: {
      title: 'Could not load the form',
      description: 'Something went wrong while loading your brands. Try again.',
      retry: 'Retry',
    },
    noReelBrandState: {
      title: 'Set up a brand first',
      description:
        'Reels are built from a brand kit: its character, products and templates. Add a brand to start your first reel.',
      action: 'Set up a brand',
    },
    reelBriefReadBack: {
      projectBriefNote: 'From a project brief',
      templateRemoved: 'Template deleted',
    },
    reelBriefInspiration: {
      title: 'Every great reel starts with clarity',
      description: 'Use this space to provide strategic and creative context behind your request.',
      goalsTitle: 'Business goals',
      goalsBody: 'What are you trying to achieve?',
      audienceTitle: 'Audience',
      audienceBody: 'Who is this for and where will it live?',
      referencesTitle: 'References',
      referencesBody: 'Share examples, styles or inspiration.',
      assetsTitle: 'Existing assets',
      assetsBody: 'Logos, footage, guidelines, etc.',
    },
  },
  reelBrandKit: {
    fieldRequired: 'Required',
    retry: 'Retry',
    saveFailed: 'Could not save your changes. Try again.',
    readOnly: 'You can view this brand kit, but only members with write access can change it.',
    durationValue: '{{seconds}} seconds',
    aspectRatioValue: '{{ratio}} ({{orientation}})',
    orientation: {
      vertical: 'Vertical',
      landscape: 'Landscape',
      square: 'Square',
      portrait: 'Portrait',
    },
    sheet: {
      cancel: 'Cancel',
      save: 'Save',
    },
    deleteDialog: {
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    sectionLoadState: {
      title: 'Could not load this section',
      description: 'Something went wrong while loading it. Try again.',
    },
    brandsError: {
      title: 'Could not load the brands',
      description: 'Something went wrong while loading your brands. Try again.',
    },
    brandList: {
      title: 'Brands',
      create: 'New brand',
      duplicate: 'Duplicate {{name}}',
      copyStatus: { copying: 'Copying…', failed: 'Copy failed' },
      delete: 'Delete {{name}}',
      emptyTitle: 'No brands yet',
      emptyDescription:
        'A brand kit holds the character, products and rules every reel is built from. Create the first one to start.',
      emptyReadOnlyDescription: 'No brand kit has been set up in this workspace yet.',
      emptyCreate: 'Create a brand',
    },
    createBrandSheet: {
      title: 'New brand',
      description: 'Choose how the brand starts, then give it a name and a slug.',
      startFrom: 'Start from',
      options: {
        blank: { label: 'New brand', hint: 'Start empty and fill in the rest from its profile.' },
        duplicate: {
          label: 'Duplicate a brand',
          hint: 'The same kit under a new name, with a creative system of its own.',
        },
        import: {
          label: 'Import from a file',
          hint: 'A brand exported from this or another workspace, made into a new brand here.',
        },
      },
      sourceBrand: 'Brand to duplicate',
      chooseSource: 'Choose a brand',
      packageFile: 'Brand package (.zip)',
      chooseFile: 'Choose a file',
      noFile: 'No file chosen yet.',
      importing: 'Importing. A brand with demo clips takes a minute.',
      importInvalid:
        'That file is not a brand package, or it was exported before whole-brand export. Export the brand again.',
      importTooLarge: 'That file is larger than 500 MB.',
      importFailed: 'The import failed and no brand was made. Try again.',
      importSuccess: 'Brand imported',
      displayName: 'Name',
      displayNamePlaceholder: 'e.g. Mr Muscle',
      slug: 'Slug',
      slugPlaceholder: 'e.g. mr-muscle',
      slugHint: 'Lowercase letters, digits, - and _. It cannot be changed later.',
      slugInvalid: 'Use lowercase letters, digits, - and _, starting with a letter or digit.',
      nameInvalid: 'Use a name of 1 to 120 characters.',
      slugTaken: 'Another brand in this workspace already uses this slug.',
      submit: {
        blank: 'Create brand',
        duplicate: 'Duplicate',
        import: 'Import brand',
      },
      success: 'Brand created',
    },
    duplicateBrandSheet: {
      title: 'Duplicate {{name}}',
      description:
        'Make a new brand from this one: the same kit under a new name, with a creative system of its own.',
      displayName: 'New brand name',
      copiedTitle: 'What comes over',
      copied: {
        guidelines: 'Documents and products',
        characters: 'Cast, references and styles',
        library: 'Demo clips, overlays, end tags, music and templates',
      },
      notCopied:
        'Campaigns, reels and the creative harness stay with this brand. The new brand starts with an empty creative harness, and its styles only cut between looks until its own harness asks for a change of look.',
      startTitle: 'How its creative harness starts',
      start: {
        empty: {
          label: 'Start empty',
          description: 'Write the creative harness yourself, or with the assistant.',
        },
        draft: {
          label: 'Draft it from a breakdown or documents',
          description:
            'Once the copy is in, type the idea, add a deck or brief, or both, and get a first draft.',
        },
      },
      duplicate: 'Duplicate',
      started: 'Copying the brand kit',
    },
    deleteBrandDialog: {
      title: 'Delete brand?',
      body: 'Delete "{{name}}" with its documents, products, references and templates? This cannot be undone.',
      inUse: 'Active reels still use this brand. Delete those reels first, then delete the brand.',
      success: 'Brand deleted',
    },
    brandEditor: {
      exportBrand: 'Export brand',
      exportFailed: 'The brand could not be exported. Try again.',
      exportTooLarge:
        "This brand's files come to more than the 500 MB an import can read. Remove some demo clips, then export it again.",
      tabsLabel: 'Brand kit sections',
      copy: {
        copying: {
          title: 'Copying {{name}}',
          description:
            'Its files are being copied from the brand it was duplicated from. The kit opens here once every one is in.',
        },
        failed: {
          title: '{{name}} was not copied',
          description:
            'The copy stopped, and nothing of the original brand came over. Delete this brand and duplicate the original again.',
        },
      },
      tabs: {
        profile: 'Profile',
        docs: 'Documents',
        products: 'Products',
        demoClips: 'Demo clips',
        chips: 'References',
        endTags: 'End tags',
        overlays: 'Overlays',
        music: 'Music',
        templates: 'Templates',
        harness: 'Creative harness',
        styles: 'Styles',
        cast: 'Cast',
      },
    },
    brandProfile: {
      groups: {
        brand: 'Brand',
        character: 'Character',
        style: 'Look and tone',
        concept: 'Concept',
      },
      slug: 'Slug',
      slugHint: 'The slug is set when the brand is created and cannot be changed.',
      fields: {
        displayName: 'Name',
        characterName: 'Character name',
        characterNameHint: 'Falls back to the brand name when empty.',
        characterId: 'Character id',
        characterIdHint: 'Falls back to the slug when empty.',
        characterPhysique: 'Character physique',
        characterDescription: 'Character description',
        wardrobeSummary: 'Wardrobe summary',
        renderStyleDirective: 'Render style',
        tone: 'Tone',
        conceptVoice: 'Concept voice',
        conceptExampleAngle: 'Example angle',
        conceptExampleSparkQuestion: 'Example spark question',
        conceptExampleSparkAnswer: 'Example spark answer',
      },
      cover: {
        title: 'Cover placeholder',
        description: 'Shown on reels that do not have a cover of their own yet.',
        fileLabel: 'Cover image file',
      },
      save: 'Save changes',
      saved: 'Brand profile saved',
    },
    imageField: {
      choose: 'Choose image',
      replace: 'Replace image',
      remove: 'Remove image',
      uploadFailed: 'Could not upload the image. Try again.',
      notInWorkspace: 'An image is not available in this workspace. Upload it again.',
    },
    brandDocs: {
      title: 'Documents',
      description:
        'The written guidance Reels Creator reads for this brand, one document at a time.',
      categoriesLabel: 'Document categories',
      categories: {
        brandGuidelines: 'Brand guidelines',
        characterGuidelines: 'Character guidelines',
        brandWorld: 'Brand world',
        culturalGuidelines: 'Cultural guidelines',
        creativeStandards: 'Creative standards',
      },
      status: {
        custom: 'Custom',
        default: 'Default',
        empty: 'Not written',
      },
      hintDefault: 'Leave this empty to use the built-in creative standards.',
      hintNone: 'Leave this empty and this document is not used.',
      save: 'Save document',
      saved: 'Document saved',
    },
    guidelineFiles: {
      title: 'Guideline files',
      description:
        "Upload brand books, style guides and claims guidance. Reels Creator reads them and fills in this brand's documents and profile, which you can still edit afterwards.",
      upload: 'Upload documents',
      reading: 'Reading and filling in…',
      fileInput: 'Guideline documents',
      empty: 'No guideline files uploaded yet.',
      listLabel: 'Uploaded guideline files',
      characters_one: '{{count}} character read',
      characters_other: '{{count}} characters read',
      truncated: 'Cut at 30,000 characters',
      removeTitle: 'Remove {{name}}?',
      removeBody:
        'Reels Creator takes out what came only from this file, then removes it. Everything else stays.',
      updated: 'Updated: {{list}}',
      nothingUpdated: 'Nothing needed changing.',
    },
    rowActions: {
      moveUp: 'Move {{name}} up',
      moveDown: 'Move {{name}} down',
      edit: 'Edit {{name}}',
      delete: 'Delete {{name}}',
    },
    brandProducts: {
      title: 'Products',
      description:
        'The products a reel can feature. Inactive products are not offered when a reel is briefed.',
      add: 'Add product',
      emptyTitle: 'No products yet',
      emptyDescription: 'Add the first product this brand sells.',
      emptyReadOnlyDescription: 'This brand has no products yet.',
      inactive: 'Inactive',
      active: 'Active: {{name}}',
      deleteTitle: 'Delete product?',
      deleteBody: 'Delete "{{name}}"? This cannot be undone.',
      deleted: 'Product deleted',
      noReference: 'No reference image',
      linkReference: 'Link a reference',
      linkReferenceFor: 'Link a reference: {{name}}',
    },
    productSheet: {
      createTitle: 'Add product',
      editTitle: 'Edit product',
      name: 'Name',
      slug: 'Slug',
      slugHint: 'Lowercase letters, digits, - and _. It cannot be changed later.',
      slugTaken: 'Another product of this brand already uses this slug.',
      description: 'Description',
      descriptionPlaceholder: 'Visual description used to keep renders on-model (optional)',
      active: 'Active',
      create: 'Create product',
      saved: 'Product saved',
      reference: 'Reference',
      noReference: 'No reference',
      referenceHint:
        'The product reference whose images picture this product. Picking one already linked to another product moves it here.',
    },
    brandChips: {
      title: 'References',
      description:
        'Named characters, products and props a storyboard can use. Each has a main image and optional extra reference images.',
      add: 'Add reference',
      emptyTitle: 'No references yet',
      emptyDescription: 'Add a character, product or prop with its images.',
      emptyReadOnlyDescription: 'This brand has no references yet.',
      types: {
        character: 'Character',
        product: 'Product',
        prop: 'Prop',
        extra: 'Extra',
        location: 'Location',
      },
      refCount_one: '{{count}} extra image',
      refCount_other: '{{count}} extra images',
      deleteTitle: 'Delete reference?',
      deleteBody: 'Delete "{{name}}" and its images? This cannot be undone.',
      deleted: 'Reference deleted',
    },
    cast: {
      title: 'Cast',
      description:
        "The characters this brand's reels can use. Each has a character sheet, approved by a person before any reel uses it.",
      newCharacter: 'New character',
      emptyTitle: 'No characters yet',
      emptyDescription:
        'Upload pictures of a character or describe one, and the app draws its character sheet.',
      emptyReadOnlyDescription: 'This brand has no characters yet.',
      filterLabel: 'Filter by kind',
      allKinds: 'All kinds',
      noMatches: 'No characters of this kind.',
      kinds: {
        real_person: 'Real person',
        photoreal_creature: 'Photoreal creature or character',
        stylised_3d: 'Stylised 3D',
        claymation: 'Claymation / stop-motion',
        illustrated_2d: '2D / illustrated',
        other: 'Other',
      },
      unclassified: 'Kind not set',
      realPersonNote: 'Real person: face checks and usage rights will apply',
      fromKit: 'Uploaded references, no sheet yet',
      status: {
        generating: 'Drawing',
        choosing: 'Pick one',
        review: 'To review',
        approved: 'Approved',
        failed: 'Failed',
      },
      progress: 'Drawing {{done}} of {{total}}',
      baseSheet: 'Base',
      lookSheet: '{{look}}',
      promptSheet: 'Variation',
      open: 'Open {{name}}',
      rename: {
        open: 'Rename {{name}}',
        field: 'Character name',
        save: 'Save',
        cancel: 'Cancel',
        required: 'Enter a name.',
        taken: 'Another character in this brand already has this name.',
      },
    },
    newCharacter: {
      nameTaken: 'Another character in this brand already has this name.',
      title: 'New character',
      startLabel: 'How do you want to start?',
      upload: 'Upload a character',
      uploadHint: 'Pictures of the character: a photo, frames from an ad, a mascot drawing.',
      describe: 'Describe a character',
      describeHint: 'Words, and mood pictures if you have them.',
      name: 'Name',
      kind: 'Kind',
      kindHint:
        'What the character natively is. Its first sheet is drawn this way, with no brand look applied.',
      kindSuggested: 'Suggested from the pictures. Change it if it is wrong.',
      kindOther: 'Describe the kind',
      pictures: 'Pictures of the character',
      picturesFile: 'Character picture files',
      moods: 'Mood pictures (optional)',
      moodsHint: 'They set the feel, never the face.',
      moodsFile: 'Mood picture files',
      addPictures: 'Add pictures',
      picture: 'Picture {{index}}',
      removePicture: 'Remove picture {{index}}',
      uploadFailed: 'Some pictures could not be uploaded.',
      description: 'Description',
      descriptionUploadHint: 'Anything the pictures do not show, such as trousers and shoes.',
      descriptionPlaceholder: 'e.g. a woman in her mid-thirties, slim, long wavy auburn hair',
      traits: 'Distinctive traits',
      traitsHint: 'What must stay the same in every view.',
      lookalikeWarning:
        'The same words give the same stock face. Name traits that make this character distinctive (a scar, a hairline, glasses, an outfit), or start from pictures.',
      expressions: 'Expressions',
      expressionsHint: 'Two or three, drawn head and shoulders.',
      expression: 'Expression {{index}}',
      addExpression: 'Add an expression',
      removeExpression: 'Remove expression {{index}}',
      defaultSmile: 'a big genuine smile',
      defaultFrown: 'a worried frown',
      create: 'Draw front views',
      cancel: 'Cancel',
    },
    characterPanel: {
      sheetsLabel: 'Sheets of this character',
      drawingCandidates: 'Drawing versions to pick from. This takes under a minute.',
      drawingViews: 'Drawing every view: {{done}} of {{total}}.',
      chooseTitle: 'Pick a front view',
      chooseHint: 'Every other view is drawn from the one you pick.',
      chooseVariationHint: 'Pick the version every view is cut from.',
      candidate: 'Version {{index}}',
      useCandidate: 'Use this one',
      newCandidates: 'Draw new ones',
      reviewTitle: 'Check every view',
      identityWarning:
        'The automatic check looks at angles, framing and the background only. It cannot tell look-alikes apart, so make sure every view is the same character before you approve.',
      views: {
        front: 'Front',
        three_quarter_left: 'Three-quarter, facing left',
        three_quarter_right: 'Three-quarter, facing right',
        side_left: 'Side, facing left',
        side_right: 'Side, facing right',
        back: 'Back',
        face: 'Face',
        expression_1: 'Expression 1',
        expression_2: 'Expression 2',
        expression_3: 'Expression 3',
      },
      checkOk: 'Check passed',
      checkFlagged: 'Check flagged: {{notes}}',
      checkFlaggedGeneric: 'Check flagged this view',
      notChecked: 'Not checked',
      remake: 'Remake {{view}}',
      approve: 'Approve sheet',
      approvedNote: 'Approved. Reels can use this sheet.',
      failedTitle: 'This sheet could not be drawn',
      failedBody: 'Try again. What was already drawn is kept.',
      retry: 'Try again',
      composite: '{{name}} character sheet',
      viewImage: '{{name}}, {{view}}',
      variationsTitle: 'Variations',
      variationsLocked:
        'Variations are drawn from the approved base sheet, so they are offered once you approve it.',
      byLook: 'In a brand look',
      byLookHint: "The same views, redrawn in one of the brand's looks.",
      pickLook: 'Choose a look',
      noLooks: 'This brand has no looks yet. Add them under Styles.',
      allLooksMade: 'Every look of this brand already has a sheet.',
      makeLook: 'Make sheet in this look',
      byPrompt: 'With a change',
      byPromptHint: 'Describe what changes: an outfit, an age, a prop.',
      makePrompt: 'Make variation',
      deleteCharacter: 'Delete character',
      deleteSheet: 'Delete this variation',
      deleteTitle: 'Delete "{{name}}"?',
      deleteCharacterBody:
        'Delete the character, its sheets and its variations? Reels already made keep their pictures.',
      deleteSheetBody: 'Delete this variation? The base sheet is kept.',
      close: 'Close',
    },
    referenceThumbnails: {
      alt: '{{name}}, reference image {{index}}',
    },
    characterReferences: {
      title: 'Character references',
      empty: 'This brand has no character references yet. Add them under References.',
    },
    chipSheet: {
      createTitle: 'Add reference',
      editTitle: 'Edit reference',
      chipKey: 'Key',
      chipKeyHint: 'Lowercase letters, digits, - and _. It cannot be changed later.',
      keyTaken: 'Another reference of this brand already uses this key.',
      nameTaken: 'Another character in this brand already has this name.',
      name: 'Name',
      type: 'Type',
      role: 'Role',
      rolePlaceholder: 'e.g. brand mascot',
      description: 'Description',
      descriptionPlaceholder: 'What the images show, so renders stay on-model (optional)',
      mainImage: 'Main image',
      mainImageHint: 'Required. It is uploaded as soon as you choose it.',
      mainImageFile: 'Main image file',
      create: 'Create reference',
      saved: 'Reference saved',
      refs: {
        title: 'Extra reference images',
        hint: 'Up to {{count}}. They are sent in this order, so say what each one shows, for example face close-up.',
        imageAlt: 'Reference image {{position}}',
        role: 'Image role',
        tightShotOnly: 'Close shots only',
        moveUp: 'Move image {{position}} up',
        moveDown: 'Move image {{position}} down',
        remove: 'Remove image {{position}}',
        fileLabel: 'Reference image files',
        add: 'Add images',
        uploadFailed: 'Could not upload {{names}}.',
      },
    },
    brandTemplates: {
      title: 'Templates',
      description:
        'A template fixes the duration and aspect ratio of a reel, and the overlays, end tag and music it uses.',
      add: 'Add template',
      emptyTitle: 'No templates yet',
      emptyDescription: 'A brand gets a Default template the first time its templates are read.',
      multiAngleOn: 'Multi-angle scene master on',
      multiAngleOff: 'Multi-angle scene master off',
      storyBeats_one: 'Story: {{count}} beat',
      storyBeats_other: 'Story: {{count}} beats',
      deleteTitle: 'Delete template?',
      deleteBody: 'Delete "{{name}}"? This cannot be undone.',
      deleted: 'Template deleted',
    },
    brandStyles: {
      title: 'Styles',
      description:
        'The looks a reel of this brand can use, and how scenes may join. Every step of the pipeline reads them.',
      learn: 'Learn from ads…',
      add: 'Add look',
      keyValue: '@{{key}}',
      default: 'Default',
      migrated: 'Migrated look',
      references_one: '{{count}} reference',
      references_other: '{{count}} references',
      transitionsTitle: 'Transitions this brand allows',
      transitionsDescription: 'Only these can be picked for a scene. A cut is always allowed.',
      transitionsSaved: 'Transitions saved',
      deleteTitle: 'Delete look?',
      deleteBody: 'Delete "{{name}}"? Reels already started keep it.',
      deleted: 'Look deleted',
      transitions: {
        cut: 'Cut',
        match_cut: 'Match cut',
        dissolve: 'Dissolve',
        look_switch: 'Look switch (same shot)',
        world_morph: 'World morph (same shot)',
      },
    },
    lookSheet: {
      createTitle: 'Add look',
      editTitle: 'Edit look',
      create: 'Add look',
      saved: 'Look saved',
      name: 'Name',
      key: 'Key',
      keyHint:
        'How the script names this look. Lowercase letters, digits and underscores; it cannot change later.',
      keyInvalid: 'Use lowercase letters, digits and underscores, starting with a letter.',
      keyTaken: 'Another look already uses this key.',
      description: 'Description',
      descriptionHint: 'What the look is, in plain words. The concept and script writers read it.',
      imageDirective: 'Image directive',
      imageDirectiveHint:
        'How pictures are rendered (light, camera, texture), not what is in them. Be strong: say what may not remain.',
      characterDirective: 'Character wording',
      characterDirectiveHint:
        'How a character sheet is remade in this look, with full strength: for clay, puppet features, sculpted hair, clothes of rolled clay, and that nothing of the original may remain. Without it, the image wording is used.',
      videoDirective: 'Video directive',
      videoDirectiveHint: 'The one line the video model reads for this look.',
      neverShow: 'Never show',
      neverShowHint:
        'Things this look must never show. Press Enter or Add after each one, up to {{count}}.',
      neverShowPlaceholder: 'e.g. glossy CGI skin',
      neverShowAdd: 'Add',
      neverShowRemove: 'Remove {{word}}',
      references: 'Reference images',
      referencesHint:
        'Up to {{count}} pictures of this look. The image model is shown them to keep frames on style.',
      referencesFile: 'Reference image files',
      referencesAdd: 'Add images',
      referencesUploadFailed: 'Could not upload {{names}}.',
      referenceAlt: 'Reference image {{position}}',
      referenceRemove: 'Remove reference image {{position}}',
      soundCues: 'Sound cues',
      soundCuesHint: 'The sound of this look. Shots in it carry these cues to the video model.',
      isDefault: 'Default look',
      isDefaultHint:
        'Scenes that name no look use the default. To change it, make another look the default.',
    },
    palette: {
      label: 'Palette',
      hint: "The look's main colours, up to {{count}}. Pick a swatch to change its colour or name.",
      add: 'Add colour',
      edit: 'Edit colour {{position}}: {{colour}}',
      hex: 'Colour (hex)',
      hexInvalid: 'Use a colour written like #e4572e.',
      picker: 'Pick a colour',
      name: 'Colour name',
      namePlaceholder: 'e.g. Warm clay orange',
      remove: 'Remove colour',
    },
    lookPreviews: {
      title: 'Style proof',
      empty:
        'No sample stills yet. Make three, a person, a place and an object drawn in this look, to check it before you approve it.',
      drawing: 'Drawing three sample stills. This can take a minute or two.',
      make: 'Make previews',
      remake: 'Make new previews',
      approve: 'Approve look',
      pendingChange:
        'These stills show the saved look. Saving your changes to the image directive, never-show list or references clears them, so make new ones after you save.',
      staleChanged:
        'This look was approved, but it has changed since and its old stills were cleared. Make new previews and approve it again.',
      staleRedrawn:
        'These stills were drawn after the look was approved. Approve it again if they look right.',
      approval: {
        approved: 'Approved',
        stale: 'Approval out of date',
        none: 'Not approved',
      },
      subjects: {
        person: 'Person',
        place: 'Place',
        object: 'Object',
      },
      alt: {
        person: 'A person drawn in {{name}}',
        place: 'A place drawn in {{name}}',
        object: 'An object drawn in {{name}}',
      },
      altNumbered: 'Sample still {{position}} of {{name}}',
      errors: {
        failed: 'No still could be drawn. Try again in a moment.',
        noStills: 'Make previews before you approve the look.',
        gone: 'This look is no longer saved on the brand. Close it and open it again.',
        generic: 'Something went wrong. Try again.',
      },
    },
    learnStyle: {
      title: 'Learn a style',
      description:
        "Upload the brand's ads and pictures. We find the looks in them and describe each one; you review the words, keep the looks you want, then approve them on sample stills.",
      steps: {
        upload: 'Upload ads and pictures',
        review: 'Review the looks',
        proof: 'Preview and approve',
      },
      dropTitle: 'Drop pictures and ads here',
      dropCaption:
        'Up to {{images}} pictures and {{videos}} ads, each video up to 200 MB. The first two minutes of each ad are studied.',
      choose: 'Choose files',
      pictures: 'Pictures ({{picked}} of {{max}})',
      ads: 'Ads ({{picked}} of {{max}})',
      remove: 'Remove {{name}}',
      skipped:
        'Not added: {{names}}. Only pictures and videos fit, up to 12 pictures and 3 ads of 200 MB at most.',
      uploading: 'Uploading {{done}} of {{total}} files…',
      uploadProgress: 'Upload progress',
      learning: 'Studying your pictures and ads. This can take a few minutes.',
      learn: 'Find looks',
      noneFound: 'No looks were found in these files. Start again with other pictures or ads.',
      found_one:
        'We found {{count}} look in {{samples}} pictures. Edit its words, then keep or discard it.',
      found_other:
        'We found {{count}} looks in {{samples}} pictures. Edit their words, then keep or discard each one.',
      draftTitle: 'Look {{position}}: {{name}}',
      decision: {
        pending: 'To review',
        kept: 'Kept',
        discarded: 'Discarded',
      },
      keep: 'Keep look',
      discard: 'Discard',
      undoDiscard: 'Undo discard',
      samplesLabel: 'Sample frames of {{name}}',
      sampleAlt: 'Sample frame {{position}} of {{name}}',
      tooMany:
        'The brand has room for {{room}} more looks ({{max}} at most). Discard some, or delete a look first.',
      save_one: 'Save {{count}} look',
      save_other: 'Save {{count}} looks',
      saved_one: 'Look saved to the brand',
      saved_other: '{{count}} looks saved to the brand',
      proofIntro:
        'Saved. Make previews of each new look to check it, then approve it. You can also do this later from the look itself.',
      startAgain: 'Start again',
      done: 'Done',
      errors: {
        sourcesInvalid: 'Add at least one picture or ad, up to 12 pictures and 3 ads.',
        notInWorkspace: 'One of the files is not in this workspace. Upload it again.',
        unreadable: 'One of the files could not be read as a picture or a video. Try another file.',
        failed: 'We could not study these files just now. Try again in a moment.',
        generic: 'Something went wrong. Try again.',
        uploadFailed: 'Could not upload {{names}}. Try again.',
      },
    },
    templateSheet: {
      createTitle: 'Add template',
      editTitle: 'Edit template',
      name: 'Name',
      nameTaken: 'Another template of this brand already uses this name.',
      duration: 'Duration',
      aspectRatio: 'Aspect ratio',
      multiAngle: 'Multi-angle scene master',
      multiAngleHint:
        'Builds angle-specific plates per continuity group. It keeps props and characters steady across angles but is slower.',
      create: 'Create template',
      saved: 'Template saved',
      overlayFolder: 'Overlay folder',
      overlayFolderHint:
        'Scenes can use any overlay in this folder. The headline overlay goes on the headline scene.',
      alwaysOnOverlay: 'Always-on overlay',
      alwaysOnHint: 'Shown on every other scene. Pick a folder first.',
      endTag: 'End tag',
      musicTrack: 'Music',
      generatedAudio: 'Music from the video model',
      generatedAudioChoice: {
        musicAndEffects: 'Let the video model add music',
        effectsOnly: 'Sound effects only (no music)',
      },
      effectsOnlyWithTrack:
        'The video model adds sound effects only. The music comes from this track.',
      none: 'None',
      missing: 'No longer in the library',
      libraryRefInvalid:
        'An overlay, end tag or music track is no longer in the library. Pick it again.',
    },
    mediaField: {
      choose: 'Choose file',
      replace: 'Replace file',
      uploadFailed: 'Could not upload the file. Try again.',
      unreadable: 'This file could not be read as audio or video, or it is over 50 MB.',
    },
    endTags: {
      title: 'End tags',
      description:
        "Short clips that close this brand's reels, played after the video with their own sound and no overlay.",
      add: 'Add end tag',
      emptyTitle: 'No end tags yet',
      emptyDescription: "Upload an MP4 or MOV clip to close this brand's reels.",
      posterAlt: 'Last frame of {{name}}',
      deleteTitle: 'Delete end tag?',
      deleteBody: 'Delete "{{name}}"? Reels that end with it will end without one.',
      deleted: 'End tag deleted',
    },
    endTagSheet: {
      title: 'Add end tag',
      name: 'Name',
      file: 'Clip',
      fileHint: 'MP4 or MOV, up to 50 MB.',
      fileLabel: 'End tag clip file',
      create: 'Add end tag',
      saved: 'End tag added',
    },
    demoClips: {
      title: 'Demo clips',
      description:
        "The brand's own product demo footage. A pack shot set to a brand clip lasts as long as its clip, and the edit can show the original in its place.",
      add: 'Add demo clip',
      emptyTitle: 'No demo clips yet',
      emptyDescription: 'Upload an MP4 or MOV of 1 to 15 seconds showing the product in use.',
      posterAlt: 'A frame of {{name}}',
      product: 'Shows {{name}}',
      deleteTitle: 'Delete demo clip?',
      deleteBody:
        'Delete "{{name}}"? Reels that play it will animate their drawn pack shot instead.',
      deleted: 'Demo clip deleted',
      sendToModel: 'Send brand demo clips to the video model',
      sendToModelHint:
        'The video model sees the clip and remakes the pack shot from it. Turned off, it only paces the shot; the original can still be shown in the edit.',
    },
    demoClipSheet: {
      title: 'Add demo clip',
      name: 'Name',
      file: 'Clip',
      fileHint: 'MP4 or MOV, 1 to 15 seconds, up to 50 MB.',
      fileLabel: 'Demo clip file',
      product: 'Product',
      noProduct: 'No product',
      create: 'Add demo clip',
      saved: 'Demo clip added',
      lengthInvalid: 'A demo clip runs from 1 to 15 seconds.',
    },
    music: {
      title: 'Music',
      description:
        "Tracks this brand's templates can use as a reel's music. Upload only music your team is licensed to use.",
      add: 'Upload track',
      emptyTitle: 'No music yet',
      emptyDescription: 'Upload an MP3 your team is licensed to use.',
      playLabel: 'Play {{title}}',
      deleteTitle: 'Delete track?',
      deleteBody: 'Delete "{{title}}"? Reels that used it will play no music.',
      deleted: 'Track deleted',
    },
    musicSheet: {
      title: 'Upload track',
      file: 'MP3 file',
      fileLabel: 'Music file',
      titleField: 'Title',
      artist: 'Artist',
      license: 'License',
      licenseHint: 'What your team may do with this track.',
      create: 'Upload track',
      saved: 'Track uploaded',
    },
    overlays: {
      title: 'Overlays',
      description: "Figma frames shown over a reel's scenes. A template picks a folder of them.",
      add: 'Import from Figma',
      folderFilter: 'Folder',
      allFolders: 'All folders',
      unfiled: 'No folder',
      emptyTitle: 'No overlays here yet',
      emptyDescription: 'Import a frame from Figma to show it over your reels.',
      previewAlt: 'Preview of {{name}}',
      textLayers_one: '{{count}} text layer',
      textLayers_other: '{{count}} text layers',
      refresh: 'Refresh {{name}} from Figma',
      deleteTitle: 'Delete overlay?',
      deleteBody: 'Delete "{{name}}"? Reels and templates that used it will show no overlay there.',
      deleted: 'Overlay deleted',
    },
    folders: {
      title: 'Folders',
      description: 'Group overlays. A template uses the overlays of one folder.',
      add: 'Add folder',
      emptyTitle: 'No folders yet',
      emptyDescription: 'Create a folder to group the overlays a template uses.',
      overlayCount_one: '{{count}} overlay',
      overlayCount_other: '{{count}} overlays',
      deleteTitle: 'Delete folder?',
      deleteBody: 'Delete "{{name}}"? Its overlays stay, without a folder.',
      deleted: 'Folder deleted',
    },
    folderSheet: {
      createTitle: 'Add folder',
      editTitle: 'Rename folder',
      name: 'Name',
      nameTaken: 'Another folder already uses this name.',
      create: 'Add folder',
      saved: 'Folder saved',
    },
    importOverlay: {
      title: 'Import from Figma',
      figmaUrl: 'Figma frame link',
      figmaUrlHint: 'Open the frame in Figma, then use "Copy link to selection".',
      token: 'Figma personal access token',
      tokenHint: 'Used for this import only and never stored.',
      browse: 'Browse frame',
      browseHint: 'Pick one part of the frame to import instead of the whole frame.',
      partLabel: 'Part of the frame to import',
      wholeFrame: 'The whole frame',
      size: '{{width}} × {{height}}',
      folder: 'Folder',
      name: 'Name',
      nameHint: "Leave empty to use the frame's name.",
      import: 'Import overlay',
      imported: 'Overlay imported',
      rejected: 'Figma refused the request. Check the link and the token.',
      invalidUrl: 'This link does not point at a frame.',
    },
    overlaySheet: {
      title: 'Edit overlay',
      name: 'Name',
      folder: 'Folder',
      saved: 'Overlay saved',
    },
    refreshOverlay: {
      title: 'Refresh from Figma',
      description: 'Reads "{{name}}" again from Figma. Text typed over it in reels stays.',
      refresh: 'Refresh',
      refreshed: 'Overlay refreshed',
    },
  },
  reelScene: {
    heading: 'Scene {{num}}: {{title}}',
    headingNoTitle: 'Scene {{num}}',
  },
  reelImage: {
    rendering: 'Rendering image',
    unavailable: 'Image unavailable',
  },
  reelConcept: {
    reelConceptColumn: {
      heading: 'Concepts and ideas',
      listLabel: 'Concepts',
      ideasCount_one: '{{count}} idea',
      ideasCount_other: '{{count}} ideas',
      regenerateAll: 'Regenerate all',
      concept: 'Concept {{letter}}',
      back: 'Back',
      next: 'Next',
      starting: 'Starting…',
    },
    regenerateDialog: {
      title: 'Regenerate all concepts?',
      body: 'This replaces every concept with new ones. A script written from the current concepts is marked outdated.',
      cancel: 'Cancel',
      confirm: 'Regenerate',
    },
    replaceScriptDialog: {
      title: 'Replace the current script?',
      body: 'This reel already has a script. Continuing writes a new one from this concept and replaces it.',
      cancel: 'Cancel',
      confirm: 'Replace script',
    },
    conceptTile: {
      redrawing: 'Updating picture…',
      redrawFailed: 'Not updated',
    },
    conceptDetail: {
      concept: 'Concept {{letter}}',
      hookTile: 'Hook',
      payoffTile: 'Payoff',
      theSpark: 'The Spark',
      openingBeat: 'Opening beat',
      culturalRelevance: 'Cultural relevance',
      culturalMoment: 'The cultural moment',
      howToExecute: 'How to execute against it',
      whyItResonates: 'Why it will resonate',
      whyItAligns: 'Why it aligns with {{brand}}',
      whyItAlignsNoBrand: 'Why it aligns with the brand',
      participation: 'How audiences will engage',
      whyNotBoring: 'Why this is not derivative',
      morphPlan: {
        title: 'Look change',
        none: 'The story stays in one look, with no transformation.',
        turnsInto: 'turns into',
        where: 'Where: {{where}}',
        changes: 'What turns into what',
        change: '{{from}} becomes {{into}}',
      },
    },
    conceptEditChat: {
      label: 'Edit instruction',
      placeholder: 'Tell us what you would like to change',
      pendingPlaceholder: 'Rewriting the concept…',
      appliedPlaceholder: 'Applied. Ask for another change, or pick a different concept.',
      apply: 'Apply edit',
      inProgress: 'This concept is still being written. Try again in a moment.',
      failed: 'Could not apply the edit. Try again.',
    },
    conceptLoading: {
      title: 'Building a creative concept',
      description: 'Analyzing your brief and files…',
    },
    conceptFailure: {
      title: 'Concept generation failed',
      fallbackMessage: 'Something went wrong while writing the concepts.',
      retry: 'Retry',
      messages: {
        provider_unavailable: 'The model did not answer. Try again.',
        generation_timed_out: 'The generation took too long. Try again.',
        input_too_large: 'The brief and brand kit are too large for the model.',
        request_blocked: 'The request was blocked by a content guardrail.',
        generation_failed: 'The generation failed. Try again.',
      },
    },
    conceptsNotGenerated: {
      title: 'No concepts yet',
      description:
        'Concepts have not been generated for this reel. Only members with write access can generate them.',
    },
  },
  reelScript: {
    sceneStyle: {
      editorTitle: 'Scene {{num}}',
      join: 'Into this scene',
      look: 'Opens in',
      lookChanges: 'Look changes inside this shot',
      lookChangesHint: 'The camera and pose hold: the shot goes on, it is not a new scene.',
      addChange: 'Add a look change',
      changeLabel: 'Look change {{index}}',
      removeChange: 'Remove look change {{index}}',
      changeType: 'Type of change',
      atSec: 'Starts at (s)',
      overSec: 'Takes (s)',
      intoLook: 'Turns into',
      cancel: 'Cancel',
      save: 'Save',
      joinMarker: 'Join between scene {{from}} and scene {{to}}: {{type}}',
      lookMarker: 'Look of scene {{num}}: {{looks}}',
      changeMarker: 'Scene {{num}} at {{at}} s: {{type}} into {{look}}',
      phasePicker: 'Picture of scene {{num}}',
      phaseOption: '{{look}} · {{at}} s',
      redraw: 'Regenerate look change',
      redrawing: 'Regenerating…',
      redrawHint:
        'Draws the pictures after each look change again from this frame. The frame itself stays.',
      compare: {
        label: 'Before and after (drag)',
        slider: 'Compare before and after',
        valueText: '{{percent}}% before',
        before: 'Before: {{look}}',
        after: 'After: {{look}}',
        missing: 'Not drawn yet',
      },
      types: {
        cut: 'Cut',
        match_cut: 'Match cut',
        dissolve: 'Dissolve',
        look_switch: 'Look switch',
        world_morph: 'World morph',
      },
    },
    reelScriptColumn: {
      heading: 'Scenes',
      listLabel: 'Scenes',
      scenesCount_one: '{{count}} scene',
      scenesCount_other: '{{count}} scenes',
      regenerate: 'Regenerate',
      back: 'Back',
      next: 'Next',
      starting: 'Starting…',
      pickedConcept: 'Concept {{letter}}',
    },
    endCard: {
      title: 'Scene {{num}}: End card',
      locked: 'Locked · added after the edit',
    },
    regenerateDialog: {
      title: 'Regenerate the script?',
      body: 'This replaces every scene with a new script written from the same concept.',
      cancel: 'Cancel',
      confirm: 'Regenerate',
    },
    outdatedAlert: {
      title: 'This script is out of date',
      description: 'The concept it was written from has changed.',
      descriptionGone: 'The concept it was written from is no longer in this reel.',
      regenerate: 'Regenerate script',
      pickConcept: 'Pick a concept',
    },
    sceneCanvas: {
      linesLabel: 'Script lines',
      super: 'Super',
      direction: 'Direction',
      scene: 'Scene',
      action: 'Action',
      inFrame: 'In frame',
      camera: 'Camera',
      characters: 'Characters',
      dialogue: 'Dialogue',
      voiceover: 'Voiceover',
      sfx: 'SFX',
      transition: 'Transition',
      superContent: '“{{text}}”, on-screen headline',
    },
    endState: {
      label: 'Where the shot ends',
      placeholder: 'For example: she holds the clean plate up to the camera, smiling',
      hint: 'The moment the end frame shows. Leave it empty to end on the action played out.',
      save: 'Save end',
      saveFailed: 'The end could not be saved. Try again.',
      empty: 'The action played out',
    },
    voOverflow: {
      description:
        'The voiceover runs {{seconds}}s past the end of this scene. A video made shot by shot cuts it at the join, so shorten the line or lengthen the scene.',
      badge: 'VO too long',
    },
    sceneGlance: {
      title: 'Scene at a glance',
      location: 'Location',
      characters: 'Characters',
      duration: 'Duration',
      durationSeconds: '{{seconds}}s',
      sfxMusic: 'SFX / Music',
      direction: 'Direction',
      labels: 'Labels',
      empty: '—',
    },
    references: {
      label: 'References',
      checking: 'Preparing references…',
      drawing: 'Drawing references ({{ready}} of {{total}})',
      ready: 'References ready',
      failed: '{{ready}} of {{total}} references ready',
      failedTitle_one: 'A reference could not be drawn',
      failedTitle_other: '{{count}} references could not be drawn',
      failedDescription_one: 'It was tried several times. Open it and press Regenerate.',
      failedDescription_other: 'Each was tried several times. Open one and press Regenerate.',
      nextBlocked: {
        checking: 'Preparing the references…',
        waiting: 'Waiting for references ({{ready}} of {{total}} ready)',
        failed: 'Regenerate the references that could not be drawn to continue',
      },
    },
    sceneEditChat: {
      label: 'Edit instruction',
      placeholder: 'Tell us what you would like to change',
      pendingPlaceholder: 'Rewriting the scene…',
      appliedPlaceholder: 'Applied. Ask for another change, or pick a different scene.',
      apply: 'Apply edit',
      inProgress: 'This script is still being written. Try again in a moment.',
      failed: 'Could not apply the edit. Try again.',
    },
    scriptLoading: {
      title: 'Writing your script',
      description: 'Turning the concept into scenes…',
    },
    scriptFailure: {
      title: 'Script generation failed',
      fallbackMessage: 'Something went wrong while writing the script.',
      retry: 'Retry',
      pickConcept: 'Pick a concept',
      messages: {
        provider_unavailable: 'The model did not answer. Try again.',
        generation_timed_out: 'The generation took too long. Try again.',
        input_too_large: 'The brief and brand kit are too large for the model.',
        request_blocked: 'The request was blocked by a content guardrail.',
        generation_failed: 'The generation failed. Try again.',
        concept_not_found:
          'The concept this script was written from is no longer in this reel. Pick a concept to write a new one.',
      },
    },
    scriptNotStarted: {
      title: 'Pick a concept on the Concept step',
      description: 'The script is written from the concept you choose. Pick one and press Next.',
      action: 'Back to concepts',
    },
  },
  reelStoryboard: {
    productDemo: {
      label: 'Product demo',
      sourceLabel: 'Product demo source',
      sources: {
        generated: 'Drawn pack shot',
        brand_clip: 'Brand clip',
      },
      clipLabel: 'Demo clip',
      clipPlaceholder: 'Choose a clip',
      noClips: "This brand has no demo clips yet. Add one in its brand kit's Demo clips.",
      noClipNote: 'Until a clip is chosen, this drawn pack shot is animated in its place.',
      thumbnailAlt: 'A frame of {{name}}',
      clipDuration: '{{seconds}} s, the length of this scene',
    },
    sheetGaps: {
      title: 'Character sheets to make',
      missing_one:
        '{{name}} has no approved {{look}} sheet. Scene {{scenes}} draws them from the base sheet meanwhile.',
      missing_other:
        '{{name}} has no approved {{look}} sheet. Scenes {{scenes}} draw them from the base sheet meanwhile.',
      pending_one:
        "{{name}}'s {{look}} sheet is waiting in the brand kit's Cast. Scene {{scenes}} uses the base sheet until it is approved.",
      pending_other:
        "{{name}}'s {{look}} sheet is waiting in the brand kit's Cast. Scenes {{scenes}} use the base sheet until it is approved.",
      makeNow: 'Make it now',
      makeNowLabel: 'Make the {{look}} sheet of {{name}} now',
    },
    reelStoryboardColumn: {
      heading: 'Frames',
      listLabel: 'Frames',
      regenerateAll: 'Regenerate all',
      preparing: 'Preparing references…',
      frameFailed: 'Frame failed',
      mayNotMatch: 'May not match its references',
      back: 'Back',
      next: 'Next',
      nextNotReady: 'The storyboard is not ready for a video yet. Check that every frame is drawn.',
      nextEndFramesMissing:
        'This reel is made shot by shot, and some shots have no end frame to end on. Draw the end frames, then try again.',
      nextFailed: 'The video could not be started. Try again.',
    },
    regenerateAllDialog: {
      title: 'Regenerate the storyboard?',
      body: 'This draws every frame again from the current script. The frames you have now stay in each frame’s versions.',
      cancel: 'Cancel',
      confirm: 'Regenerate',
    },
    regenerateFrameDialog: {
      title: 'Regenerate this frame?',
      body: 'This draws the frame again and makes the new one active. Earlier versions stay in the version strip.',
      cancel: 'Cancel',
      confirm: 'Regenerate',
    },
    outdatedAlert: {
      title: 'This storyboard is out of date',
      description: 'The script it was drawn from has changed.',
      descriptionScene: 'Scene {{scene}} was changed since it was drawn.',
      descriptionScriptNotReady:
        'The script it was drawn from has changed and is not finished yet. Regenerate once it is ready.',
      regenerate: 'Regenerate storyboard',
    },
    storyboardCarousel: {
      label: 'Storyboard frames',
      imagesGenerated_one: '{{count}} image generated',
      imagesGenerated_other: '{{count}} images generated',
      seconds: '{{seconds}}s',
      prompt: 'Prompt',
      promptPlaceholder: 'Type what you want to change',
      regenerateImage: 'Regenerate image',
      regenerating: 'Regenerating…',
      previous: 'Previous frame',
      next: 'Next frame',
      endCard: 'End card',
      endCardAlt: 'End card: {{name}}',
      endCardLocked: 'From your template, not editable',
    },
    advanced: {
      modeToggle: {
        label: 'Storyboard view',
        basic: 'Basic',
        advanced: 'Advanced',
      },
      grid: {
        shot: 'Shot {{num}}',
        openEditor: 'Open editor',
        version: 'v{{version}}',
        versionHint: 'v{{version}}: open the version stepper',
        reorderFailed: 'Could not change the scene order.',
        regenerate: 'Regenerate',
        generate: 'Generate',
        moveEarlier: 'Move earlier',
        moveLater: 'Move later',
        notInRender: '(not in this render yet)',
      },
      camera: {
        heading: 'Camera',
        edited: 'edited',
        apply: 'Apply & regenerate',
        reset: 'Reset',
        shotType: 'Shot type',
        lens: 'Lens',
        depthOfField: 'Depth of field',
        angle: 'Camera angle',
        shotTypes: {
          ecu: 'Extreme close-up (ECU)',
          cu: 'Close-up (CU)',
          mcu: 'Medium close-up (MCU)',
          ms: 'Medium shot (MS)',
          mws: 'Medium wide (MWS)',
          ws: 'Wide shot (WS)',
          ews: 'Extreme wide (EWS)',
          ots: 'Over-the-shoulder (OTS)',
          pov: 'Point of view (POV)',
          insert: 'Insert/detail',
        },
        angles: {
          eye_level: 'Eye level',
          low: 'Low angle',
          high: 'High angle',
          dutch: 'Dutch/tilted',
          birds_eye: "Bird's eye",
          worms_eye: "Worm's eye",
        },
        depths: {
          deep: 'Deep (everything sharp)',
          moderate: 'Moderate',
          shallow: 'Shallow (bokeh background)',
          very_shallow: 'Very shallow (strong bokeh)',
        },
      },
      intent: {
        heading: 'Intent prompt',
        empty: 'No intent prompt yet. Edit to write one.',
        edit: 'Edit intent',
        cancel: 'Cancel',
        rebuild: 'Rebuild from intent',
        label: 'Intent',
        placeholder: 'Plain-language description of the shot. The scene is rewritten around it.',
      },
      prompt: {
        heading: 'View the prompt sent to the image model',
        empty: 'No prompt yet',
        length: '{{count}} characters',
        hint: 'The literal text the image model received.',
        edit: 'Edit',
        cancel: 'Cancel',
        regenerate: 'Regenerate with edit',
        label: 'Prompt',
      },
      lightbox: {
        title: 'Shot {{num}} · v{{version}}',
        qc: 'QC {{verdict}}',
        download: 'Download',
        close: 'Close',
        edit: 'Edit',
        inpaint: 'Inpaint',
        editPrompt: 'Edit prompt',
        cancel: 'Cancel',
        editHeading: 'Chat edit',
        editLabel: 'What to change',
        editPlaceholder: 'Describe the change (for example, move the bartender to the left)',
        addReference: 'Add reference',
        references_one: '{{count}} reference',
        references_other: '{{count}} references',
        clearReferences: 'Clear references',
        referenceRefused: 'Only PNG, JPEG or WebP images up to 10 MB can be added.',
        applyEdit: 'Apply edit',
        inpaintHeading: 'Inpaint',
        inpaintHint: 'Paint white over the area to replace, then describe what to put there.',
        inpaintLabel: 'What to put in the painted area',
        maskLabel: 'Paint the area to replace',
        clearMask: 'Clear mask',
        applyInpaint: 'Apply inpaint',
        promptHeading: 'Prompt sent to the image model',
        regenerate: 'Regenerate',
        sceneDirection: 'Scene direction',
        dialogue: 'Dialogue',
        qcHeading: 'QC per chip',
        noChips: 'No chip expectations for this shot.',
        score: '{{score}}',
        hero: 'Character identity',
        regenerateShot: 'Regenerate this shot',
        verdicts: {
          pass: 'Pass',
          borderline: 'Borderline',
          fail: 'Fail',
          qc_error: 'Not checked',
        },
      },
      masters: {
        heading: 'Scene Master',
        groupHint_one:
          '{{count}} shot shares this location. Shots re-frame this plate; open the hero or an alternate to edit it.',
        groupHint_other:
          '{{count}} shots share this location. Shots re-frame this plate; open the hero or an alternate to edit it.',
        regenerate: 'Regenerate Scene Master',
        hero: 'Hero',
        alternate: 'Low angle',
        notYet: 'not yet',
        single:
          'No Scene Master generated: only one shot relates to this scene. Scene Masters are only created for locations shared by 2 or more shots.',
        openHero: 'Open the hero Scene Master: editing it regenerates every alternate',
        openAlternate: 'Open the {{label}} alternate: edit just this image',
        lockedHero: 'Locked while alternates finish regenerating',
        lockedAlternate: 'Locked while the hero regenerates',
        regenerating: 'Regenerating…',
        alt: 'Scene Master {{label}}',
        dialogHeroTitle: 'Hero Scene Master',
        dialogAlternateTitle: '{{label}} alternate',
        dialogHeroBody: 'Editing the hero regenerates every alternate in this scene.',
        dialogAlternateBody: 'Only this alternate image is regenerated.',
        promptLabel: 'Change',
        heroPlaceholder:
          'Describe the change to the hero plate (for example, add warm string lights along the back wall)',
        alternatePlaceholder:
          'Describe the change to this alternate (for example, make the surface darker walnut)',
        emptyHint: 'Leave empty to regenerate with the same prompt as before.',
        editHero: 'Edit hero and regenerate alternates',
        editAlternate: 'Edit this image',
        regenerateHero: 'Regenerate hero and alternates',
        regenerateAlternate: 'Regenerate',
        close: 'Close',
      },
    },
    styleCheck: {
      drift: 'Off style',
      driftFallback: 'This frame drifts from its look.',
      checkError: 'Style not checked',
      checkErrorDetail: 'The style check could not judge this frame.',
      retried: 'It was already drawn once more.',
    },
    storyboardCanvas: {
      driftTitle: 'May not match its references',
      frameAlt: 'Frame for {{scene}}',
      applying: 'Applying…',
      versionsLabel: 'Versions',
      version: 'v{{version}}',
    },
    frameEditChat: {
      label: 'Edit instruction',
      placeholder: 'Tell us what you would like to change',
      pendingPlaceholder: 'Applying…',
      appliedPlaceholder: 'Applied. Ask for another change, or pick a different frame.',
      apply: 'Apply edit',
    },
    remakeShots: {
      title: 'Make the changed shots again?',
      body_one:
        'Shot {{shots}} changed since the video was made, so it will be made again. Every other shot keeps its take.',
      body_other:
        'Shots {{shots}} changed since the video was made, so they will be made again. Every other shot keeps its take.',
      cancel: 'Cancel',
      confirm: 'Make them again',
    },
    filmstrip: {
      title: 'Frames in order',
      framesLabel: 'Start and end frames',
      shotsLabel: 'Shot lengths',
      startFrame: 'Shot {{num}} start',
      endFrame: 'Shot {{num}} end',
      linkedFrame: 'Shot {{from}} end, shot {{to}} start',
      linked: 'Linked',
      notDrawn: 'Not drawn yet',
      shot: 'Shot {{num}}',
      shotLength: 'Shot {{num}} · {{seconds}}s',
      voOverShort: 'VO +{{seconds}}s',
      voOver: 'The voiceover runs {{seconds}}s past the end of this shot and is cut at the join.',
      missingEnds_one: '{{count}} shot has no end frame',
      missingEnds_other: '{{count}} shots have no end frame',
      drawEndFrames: 'Draw end frames',
      showingEnd: 'End frame',
    },
    frameReference: {
      label: 'Storyboard reference',
      options: {
        soft: {
          label: 'Soft',
          description:
            'Start and end frames sent as references. Slight changes show, but the result is usually better.',
        },
        hard: {
          label: 'Hard',
          description:
            'Start and end frames exactly as on the storyboard. The animation can feel unnatural.',
        },
        start: {
          label: 'Start only',
          description: 'The shot is made from its start frame alone.',
        },
      },
      saveFailed: 'Could not save. Try again.',
    },
    referenceLimit: {
      title: 'Too many reference images for the frame image model',
      fallback: 'Some frames need more reference images than the frame image model takes.',
      hint: 'Use fewer references in those scenes, or pick a frame image model that takes more.',
    },
    actionErrors: {
      inProgress: 'The storyboard is still being drawn. Try again in a moment.',
      notFound:
        'This frame or version is no longer there. Try again once the storyboard refreshes.',
      invalid: 'That request was not accepted. Keep the instruction under 2,000 characters.',
      invalidAction: 'That request was not accepted. Try again once the storyboard refreshes.',
      failed: 'Could not apply the change. Try again.',
      referencesOverLimit:
        'This frame needs more reference images than the frame image model takes.',
      uploadFailed: 'An image for this change did not upload. Try again.',
    },
    frameFailure: {
      title: 'This frame could not be drawn',
      editTitle: 'This edit could not be applied',
      fallbackMessage: 'Something went wrong while drawing this frame.',
      retry: 'Retry',
      messages: {
        image_declined:
          'The image model declined to draw this frame. Try again, or edit the scene.',
        provider_refused: 'The image service refused the request. Try again.',
        timed_out: 'Drawing the frame took too long. Try again.',
        no_location_plate:
          'This scene has no location to draw it in. Give the scene a location and try again.',
        render_failed: 'The frame could not be drawn. Try again.',
      },
    },
    storyboardGlance: {
      title: 'In this frame',
      references: 'References',
      noReferences: 'No references in this frame',
      chipType: '({{type}})',
      chipTypeBrand: '({{type}}, from the brand kit)',
      direction: 'Direction',
      duration: 'Duration',
      durationSeconds: '{{seconds}}s',
      empty: '—',
      regenerateFrame: 'Regenerate frame',
      drift: 'What may not match',
      heroIdentity: 'Character identity',
      heroFeatureOther: 'Character {{feature}}',
      heroFeatures: {
        hair: 'Character hair',
        eyewear: 'Character eyewear',
        face: 'Character face',
        outfit: 'Character outfit',
        build: 'Character build',
      },
    },
    storyboardLoading: {
      title: 'Drawing your storyboard',
      description: 'Preparing references…',
    },
    storyboardFailure: {
      title: 'Storyboard generation failed',
      fallbackMessage: 'Something went wrong while drawing the storyboard.',
      retry: 'Retry',
      messages: {
        provider_unavailable: 'The image model did not answer. Try again.',
        generation_timed_out: 'The generation took too long. Try again.',
        input_too_large: 'The script and brand kit are too large for the model.',
        request_blocked: 'The request was blocked by a content guardrail.',
        generation_failed: 'The generation failed. Try again.',
        no_frame_rendered: 'No frame could be drawn. Try again.',
      },
    },
    storyboardReadError: {
      title: 'Could not load the storyboard',
      description: 'Something went wrong while loading the storyboard. Try again.',
      retry: 'Retry',
    },
    storyboardNotStarted: {
      title: 'No storyboard yet',
      description: 'The storyboard draws a frame for every scene of the script.',
      action: 'Generate storyboard',
    },
    chipSheet: {
      imageAlt: 'Image of {{name}}',
      rendering: 'Generating reference…',
      brandNote: 'This reference comes from the brand kit, so it can only be changed there.',
      description: 'Description',
      descriptionPlaceholder:
        'What this reference looks like, so every frame draws it the same way',
      regenerate: 'Regenerate',
      edit: {
        title: 'Edit image',
        label: 'Edit instruction',
        placeholder: 'Tell us what you would like to change',
        pendingPlaceholder: 'Drawing the reference…',
        appliedPlaceholder: 'Applied. Ask for another change.',
        apply: 'Apply edit',
      },
      usage_one: 'Used in {{count}} frame',
      usage_other: 'Used in {{count}} frames',
      updateFrames: 'Update frames',
      close: 'Close',
      failure: {
        title: 'This reference could not be drawn',
        fallbackMessage: 'Something went wrong while drawing this reference.',
        messages: {
          image_declined:
            'The image model declined to draw this reference. Change the description and try again.',
          provider_refused: 'The image service refused the request. Try again.',
          timed_out: 'Drawing the reference took too long. Try again.',
          no_location_plate: 'This reference could not be drawn. Try again.',
          render_failed: 'The reference could not be drawn. Try again.',
        },
      },
      actionErrors: {
        inProgress:
          'The references or the storyboard are still being drawn. Try again in a moment.',
        notFound:
          'This reference or version is no longer there. Try again once the storyboard refreshes.',
        invalid:
          'That request was not accepted. Keep the description and instruction short enough.',
        failed: 'Could not apply the change. Try again.',
        referencesOverLimit: 'This needs more reference images than the frame image model takes.',
        uploadFailed: 'An image for this change did not upload. Try again.',
      },
      refs: {
        title: 'Your reference images',
        hint: 'Up to {{count}}. Add pictures that show this reference the way it should look.',
        imageAlt: 'Reference image {{position}}',
        remove: 'Remove image {{position}}',
        fileLabel: 'Reference image files',
        add: 'Add images',
        uploadFailed: 'Could not upload {{names}}.',
      },
    },
  },
  reelStudio: {
    studioTopBar: {
      title: '{{title}} · {{aspect}}',
      heading: 'Studio',
      backToReels: 'Back to reels',
      editStoryboard: 'Edit Storyboard',
    },
    export: {
      trigger: 'Submit to Render',
      triggerHint:
        'Render the reel, review it, then send it to a Monks.Flow project folder for review.',
      rendering: 'Rendering {{progress}}%…',
      submitting: 'Submitting…',
      readOnly: 'You can view this reel but not submit it to a project.',
      needsVideo: 'Generate the video first. Submit to Render becomes active once it is ready.',
      progress: {
        title: 'Rendering Video',
        heading: 'Rendering your video',
        body: 'Please wait while we generate your video.',
        label: 'Render progress',
        cancel: 'Cancel',
      },
      review: {
        title: 'Review Video',
        player: 'Rendered reel',
        noVideo: 'No rendered clip available yet',
        ready: 'Your video is ready for export',
        hint: 'Please review it before submitting.',
        cancel: 'Cancel',
        save: 'Save',
      },
      save: {
        title: 'Save & Send to Approval',
        projectLabel: 'Project:',
        projectButton: 'Project: {{name}}',
        selectProject: 'Select project',
        searchProjects: 'Search projects',
        noUploadPermission: "You can't upload files to this project.",
        search: 'Search',
        searchLabel: 'Search folders',
        createFolder: 'Create new folder',
        chooseProject: 'Choose a project to see its folders.',
        folders: 'Folders',
        name: 'Name',
        subfolders: 'Subfolders',
        comments: 'Comments',
        status: 'Status',
        modified: 'Date modified',
        expand: 'Expand {{name}}',
        collapse: 'Collapse {{name}}',
        noFolders: 'No folders in this project.',
        noMatch: 'No folders match "{{term}}".',
        loadFailed: "Couldn't load folders. Try again.",
        cancel: 'Cancel',
        submit: 'Submit',
        submitting: 'Submitting…',
      },
      submitted: {
        title: 'Video Submitted',
        heading: 'Video Submitted Successfully',
        body: 'You can check the progress in the Projects page.',
        savedTo: 'Saved to {{project}} in {{folder}}',
        viewProject: 'View Project',
      },
      renderFailed: {
        media_unavailable: 'The video is no longer available. Generate it again, then render.',
        render_failed: 'The render failed. Try again.',
      },
      renderRefused: {
        video_not_ready: 'Generate the video first, then render it.',
        unknown: 'The render could not start. Try again.',
      },
      failed: {
        video_unavailable: 'The video is no longer available. Generate it again, then save.',
        file_name_taken:
          'A file with this name is already in that folder. Rename the reel and try again.',
        upload_failed: 'The upload did not go through. Try again.',
        render_failed: 'The final render failed. Try again.',
        export_failed: 'Saving to the project failed. Try again.',
      },
      refused: {
        project_upload_forbidden: "You can't upload files to that project.",
        project_not_found: 'That project or folder no longer exists.',
        export_in_progress: 'This reel is already being saved.',
        video_not_ready: 'Generate the video first, then save it to a project.',
        unknown: 'Saving to the project could not start. Try again.',
      },
    },
    segmentDetails: {
      heading: 'Segment Details',
      scene: 'Scene',
      name: 'Name',
      duration: 'Duration',
      seconds: 's',
      sceneName: 'Scene {{num}}: {{title}}',
      sceneNameNoTitle: 'Scene {{num}}',
      noScene: 'The scene under the playhead shows here once the video is ready.',
      back: 'Back to Storyboard',
      shot: 'Shot',
      shotNote: 'What should change?',
      shotNotePlaceholder: 'Optional, for example a slower push in',
      shotRegenerate: 'Make another take',
      shotGenerating: 'Making a new take. The shot keeps playing the one it has.',
      shotFailed: 'The new take failed, so the shot keeps the one it had.',
      shotStartFailed: 'The new take could not be started. Try again.',
      shotActivateFailed: 'That take could not be picked. Try again.',
      shotSlicing: 'Cutting the video into shots…',
      overlay: 'Overlay',
      overlayLabel: 'Overlay for this scene',
      overlayNone: 'No overlay',
      overlayAlwaysOn: '{{name}} (always on)',
      overlayNoFolder: 'The template has no overlay folder, so there are no overlays to choose.',
      overlayDetach: 'Detach',
      layers: 'Layers',
      layersNone: 'Pick an overlay to edit its layers.',
      layerText: 'Text for {{name}}',
      layerHide: 'Hide {{name}}',
      layerShow: 'Show {{name}}',
      layerBackground: 'Background elements',
      layerVideo: 'Video',
      layerKindPreviewOnly: 'Preview only',
      layerKindText: 'Text layer',
      layerKindImage: 'Image layer',
      layerColor: 'Text colour',
      layerColorFor: 'Text colour for {{name}}',
      layerOutline: 'Outline',
      layerOutlineWidthFor: 'Outline width for {{name}}',
      layerOutlineColorFor: 'Outline colour for {{name}}',
      layerOutlineWidth: '{{value}} px',
      layerReset: 'Reset',
      layerResetColor: 'Reset the text colour of {{name}} to the design',
      layerResetOutline: 'Reset the outline of {{name}} to the design',
      animation: 'Animation',
      animationIn: 'In',
      animationOut: 'Out',
      animationTypeFor: '{{edge}} animation for {{name}}',
      animationDirectionFor: '{{edge}} direction for {{name}}',
      animationAtFor: '{{edge}} time for {{name}}',
      animationDurationFor: '{{edge}} duration for {{name}}',
      animationAt: 'At (s)',
      animationDuration: 'Duration (s)',
      animationType: {
        none: 'None',
        fade: 'Fade',
        slide: 'Slide',
        scale: 'Scale',
        pop: 'Pop',
        zoom: 'Zoom',
        blur: 'Blur',
        rotate: 'Rotate',
      },
      animationDirection: {
        center: 'Center',
        up: 'Up',
        down: 'Down',
        left: 'Left',
        right: 'Right',
      },
    },
    sourceMedia: {
      heading: 'Source Media',
      images: 'Available Images ({{total}})',
      imagesEmpty: 'No frames yet. Finish the storyboard step first.',
      imagesReadError: 'Could not load the frames.',
      imagesRetry: 'Retry',
      frameAlt: 'Scene {{num}}',
      frameBadge: 'S{{num}}',
      endFrameAlt: 'Scene {{num}} end frame',
      endFrameBadge: 'S{{num}} end',
      expandFrame: 'Expand {{frame}}',
      videos: 'Available Videos ({{total}})',
      videosEmpty: 'No videos yet.',
      videosLoadMore: 'Load more videos',
      version: 'V{{version}}',
      versionDuration: '{{seconds}}s',
      versionGenerating: 'Generating…',
      versionFailed: 'Failed',
      cutsDegraded: 'The scene markers may not match the cuts in this video.',
      rerun: 'Re-run video generation',
      activateFailed: 'Could not switch to that version. Try again.',
    },
    studioTimeline: {
      label: 'Timeline',
      transitions: 'Transitions and look changes',
      restart: 'Restart',
      backOneSecond: 'Back 1 second',
      play: 'Play',
      pause: 'Pause',
      forwardOneSecond: 'Forward 1 second',
      loop: 'Loop',
      playhead: 'Playhead',
      playheadValue: '{{seconds}}s of {{total}}s',
      mark: '{{seconds}}s',
      scenes: 'Scenes',
      sceneLabel: 'Scene {{num}}',
      sceneLabelWithRole: '{{role}} · Scene {{num}}',
      timeRange: '{{start}}s – {{end}}s',
      locked: 'Locked',
      endTag: 'End tag',
      music: 'Music',
      musicTrack: '{{title}} · {{artist}}',
      modeToggle: {
        label: 'Studio view',
        basic: 'Basic',
        advanced: 'Advanced',
      },
      overlays: 'Overlays',
      overlayClip: '{{name}} · Shot {{num}}',
      overlayBadge: 'Overlay',
      demos: 'Product demos',
      demoBadge: 'Demo',
      demoPicture: 'Picture of scene {{num}}, demo clip {{name}}',
      demoPictures: {
        generated: 'Seedance version',
        original: 'Original demo',
      },
      sound: 'Scene sound',
      soundLevelScene: 'Sound level, scene {{num}}',
      soundLevelEndTag: 'Sound level, end tag',
      decibels: '{{value}} dB',
      muteScene: 'Mute scene {{num}}',
      unmuteScene: 'Unmute scene {{num}}',
      muteEndTag: 'Mute the end tag',
      unmuteEndTag: 'Unmute the end tag',
      trimOverlayStart: "Start of scene {{num}}'s overlay",
      trimOverlayEnd: "End of scene {{num}}'s overlay",
      trimMusicStart: 'Start of the music',
      trimMusicEnd: 'End of the music',
      trimEdgeAt: '{{seconds}} s',
    },
    rerunDialog: {
      title: 'Re-run video generation?',
      body: 'This makes a new version of the video from the current storyboard. The versions you have now stay in the versions list.',
      cancel: 'Cancel',
      confirm: 'Re-run',
    },
    outdatedAlert: {
      title: 'This video is out of date',
      description: 'The storyboard has changed since this video was made.',
      regenerate: 'Regenerate video',
    },
    videoPlayer: {
      label: 'Reel video, version {{version}}',
      shotLabel: 'Reel shot {{num}}, take {{version}}',
      endTagLabel: 'End tag video: {{name}}',
      demoClipLabel: 'Original demo clip: {{name}}',
    },
    shotProgress: {
      label: 'Shots of this video',
      segment: 'Shot {{num}}: {{status}}',
      detailTitle: 'Shot {{num}} · {{title}}',
      detailNum: 'Shot {{num}}',
      hint: 'Hover over or focus a shot to see how it got on.',
      retry: 'Retry shot {{num}}',
      retryFailed: 'The shot could not be started again. Try again.',
      statuses: {
        kept: 'Kept',
        queued: 'Queued',
        making: 'Making',
        ready: 'Made',
        failed: 'Failed',
      },
    },
    videoLoading: {
      title: 'Making your video',
      description: 'This can take several minutes. You can leave this page and come back.',
      elapsed: 'Elapsed {{time}}',
    },
    videoFailure: {
      title: 'Video generation failed',
      fallbackMessage: 'Something went wrong while making the video.',
      regenerate: 'Regenerate',
      back: 'Back to Storyboard',
      messages: {
        content_rejected:
          'The video model rejected the frames or the script. Change them and try again.',
        generation_timed_out: 'The video took too long to make. Try again.',
        provider_failed: 'The video model could not make the video. Try again.',
        frame_missing:
          'A frame of the storyboard could not be read. Check the storyboard and try again.',
        video_unavailable: 'The video was made but could not be fetched. Try again.',
        frames_not_trusted:
          'Frames with real faces must be registered with BytePlus first, and registering them failed. Try again.',
        frames_quota_exhausted:
          "BytePlus's account limit for trusted pictures is used up, so frames with real faces could not be sent. Ask the account owner to raise it, then try again.",
        end_frames_missing:
          'Some shots have no end frame, and a video made shot by shot ends each shot on one. Draw the end frames on the storyboard, then try again.',
        video_provider_not_configured:
          'This server has no video provider set up, so no video can be made yet. Contact support.',
      },
    },
    videoReadError: {
      title: 'Could not load the video',
      description: 'Something went wrong while loading the video. Try again.',
      retry: 'Retry',
    },
    videoUnavailable: {
      title: 'Video unavailable',
      description: 'The video was made but could not be loaded. Try again.',
      retry: 'Retry',
    },
    videoNotStarted: {
      title: 'No video yet',
      description: 'The video is made from the frames of your storyboard.',
      action: 'Generate video',
    },
  },
  reelsCreatorWizardPage: {
    columnTitle: 'Create',
    backToReels: 'Back to reels',
    mainTitle: 'New reel - {{title}}',
    collapseColumn: 'Collapse left panel',
    expandColumn: 'Expand left panel',
    collapseAside: 'Collapse right panel',
    expandAside: 'Expand right panel',
    newReel: 'New reel',
    stepsLabel: 'Steps',
    steps: {
      brief: 'Brief',
      concept: 'Concepts',
      script: 'Script',
      storyboard: 'Storyboard',
      studio: 'Studio',
    },
    readOnly: {
      title: 'Only members with write access can create reels',
      description:
        'You can open the reels in this workspace, but creating a new one needs write access. Ask a workspace admin if you need it.',
    },
    sessionError: {
      title: 'Could not load this reel',
      description: 'Something went wrong while loading this reel. Try again.',
      retry: 'Retry',
    },
  },
  creativeHarness: {
    list: {
      noPlaybook: 'No playbook: prompts work as before',
      create: 'Create playbook',
      defaultName: '{{brand}} playbook',
      loadFailed: 'The playbooks could not be loaded.',
      retry: 'Retry',
    },
    draftForm: {
      intro:
        'Describe the new idea, add documents about it, or both, and a first draft of the playbook is written for you to review and approve. It adds a change of look only where you ask for one.',
      breakdown: 'The idea',
      breakdownPlaceholder:
        'What the ads are about, how they should feel, and any moment where the world or the look changes.',
      documents: 'Documents',
      addDocument: 'Add a document',
      removeDocument: 'Remove {{name}}',
      submit: 'Draft the playbook',
      drafting: 'Drafting the playbook. This takes up to a minute.',
      startEmpty: 'Start empty instead',
      unreadable: '{{name}} could not be read. Try a PDF, Word or text file.',
      exists: 'This brand has a playbook already.',
      failed: 'The draft could not be written. Try again, or start empty.',
    },
    header: {
      approvedVersion: 'Approved v{{version}}',
      draftVersion: 'Draft v{{version}}',
      noneApproved: 'Nothing approved yet: new reels of this brand use no playbook.',
    },
    tabs: {
      label: 'Creative harness sections',
      playbook: 'Playbook',
      versions: 'Versions',
      testBench: 'Test bench',
    },
    editor: {
      timing: '{{min}}-{{max}} s',
      viewing: 'Viewing v{{version}}',
      draft: 'Draft',
      approved: 'Approved',
      frozen: 'Approved versions are frozen. Start a new draft to change this one.',
      readOnly: 'You can view this playbook but not change it.',
      newDraft: 'New draft from this version',
      save: 'Save',
      approve: 'Approve',
      saved: 'Draft saved',
      approvedToast: 'Version {{version}} approved. New reels of this brand use it.',
      invalid: 'The playbook could not be saved. Check the highlighted rules: {{detail}}',
      changeNote: 'Change note',
      changeNotePlaceholder: 'What changed in this version',
      idea: 'Idea',
      ideaHint: 'One line: what the campaign platform is about.',
      formula: 'Formula',
      formulaHint:
        'The beats every spot follows, in order. Each bar is as wide as its share of the spot.',
      addBeat: 'Add beat',
      removeBeat: 'Remove beat {{index}}',
      moveUp: 'Move beat {{index}} up',
      moveDown: 'Move beat {{index}} down',
      beatRole: 'Beat {{index}} name',
      beatIntent: 'Beat {{index}}: what it must do',
      beatMin: 'Beat {{index}} starts at (s)',
      beatMax: 'Beat {{index}} ends at (s)',
      beatLook: 'Beat {{index}} look',
      beatTransition: 'Beat {{index}} joined by',
      beatKind: 'Beat {{index}} written by',
      anyLook: 'Any look',
      noTransition: 'None',
      beatShot: 'Beat {{index}} shot',
      anyShot: 'Any shot',
      beatFraming: 'Beat {{index}} framing',
      beatFramingPlaceholder: 'What the shot frames, e.g. the hand taking the pack, front-on',
      shots: {
        ECU: 'Extreme close-up (ECU)',
        CU: 'Close-up (CU)',
        MCU: 'Medium close-up (MCU)',
        MS: 'Medium shot (MS)',
        MWS: 'Medium wide (MWS)',
        WS: 'Wide shot (WS)',
        EWS: 'Extreme wide (EWS)',
      },
      beatDemoSource: 'Beat {{index}} demo by default',
      demoSources: {
        generated: 'Drawn pack shot',
        brand_clip: 'Brand clip',
      },
      beatDemoClip: 'Beat {{index}} demo clip',
      noDemoClip: 'No clip chosen',
      demoClipGone: 'A clip the brand no longer has',
      beatLookChange: 'Beat {{index}} look change',
      lookChangeSpeeds: {
        default: 'Look change: default timing',
        fast: 'Look change: fast morph',
        blend: 'Look change: blend',
      },
      beatLookChangeAt: 'Beat {{index}} look change at % of the shot',
      lookChangeAtSuffix: '% of the shot',
      beatCarries: 'Beat {{index}} keeps the metaphor of',
      carriesNothing: 'No earlier metaphor',
      carriesFrom: 'Beat {{index}}: {{role}}',
      beatProduct: 'Beat {{index}} product',
      productRules: {
        allowed: 'Product allowed',
        required: 'Product required',
        never: 'Never the product',
      },
      kinds: {
        generated: 'The agents',
        fixed: 'Product demo',
        end_tag: 'End tag (template)',
      },
      transitions: {
        cut: 'Cut',
        match_cut: 'Match cut',
        dissolve: 'Dissolve',
        look_switch: 'Look switch in place',
        world_morph: 'World morph in place',
      },
      coreRule: 'The core rule',
      coreRuleHint: 'The one rule every concept must honour above all.',
      mustHaves: 'Must-haves',
      linesHint: 'One per line.',
      families: 'Metaphor families',
      familiesHint: 'One line each, with the examples already used.',
      addFamily: 'Add family',
      removeFamily: 'Remove family {{index}}',
      familyName: 'Family {{index}} name',
      familyLine: 'Family {{index}}: how it works',
      familyExamples: 'Family {{index}}: examples already used',
      neverList: 'Never reuse (already done)',
      tone: 'Tone',
      exemplars: 'Past spots',
      exemplarsHint:
        'Short summaries. The writer learns the formula from them, never the metaphors.',
      addExemplar: 'Add past spot',
      removeExemplar: 'Remove past spot {{index}}',
      exemplarTitle: 'Past spot {{index}} title',
      exemplarSummary: 'Past spot {{index}} summary',
      coreMove: 'Core move',
      coreMoveHint:
        'The parts every idea must fill, in order. The writer fills each one and the checker reads them.',
      coreMoveName: 'Core move name',
      addCoreMove: 'Add core move',
      removeCoreMove: 'Remove core move',
      noCoreMove: 'No core move: ideas are not split into parts.',
      addPart: 'Add part',
      partLabel: 'Part {{index}} name',
      partDescription: 'Part {{index}}: what it must do',
      movePartUp: 'Move part {{index}} up',
      movePartDown: 'Move part {{index}} down',
      removePart: 'Remove part {{index}}',
      scorecard: 'Scorecard',
      scorecardHint:
        'Each criterion shows what a weak idea and a great one look like. The checker marks ideas against these examples.',
      passScore: 'Pass mark',
      passScoreHint:
        'An idea passes at this weighted score or more, with no criterion under its floor.',
      addCriterion: 'Add criterion',
      removeCriterion: 'Remove criterion {{index}}',
      criterionName: 'Criterion {{index}} name',
      criterionDescription: 'Criterion {{index}}: what it marks',
      weight: 'Weight',
      criterionWeight: 'Criterion {{index}} weight',
      weightValue: 'Weight {{value}}',
      floor: 'Floor',
      criterionFloor: 'Criterion {{index}} floor',
      noFloor: 'None',
      lowLooksLike: 'A 1 looks like',
      highLooksLike: 'A 5 looks like',
      lowExample: 'Criterion {{index}}: an idea that scores 1',
      lowWhy: 'Criterion {{index}}: why it scores 1',
      highExample: 'Criterion {{index}}: an idea that scores 5',
      highWhy: 'Criterion {{index}}: why it scores 5',
      whyPlaceholder: 'Why',
      hardRules: 'Hard rules',
      hardRulesHint: 'Pass or fail. An idea that breaks one is thrown out before scoring.',
      addHardRule: 'Add rule',
      hardRule: 'Hard rule {{index}}',
      removeHardRule: 'Remove hard rule {{index}}',
      noHardRules: 'No hard rules yet.',
      fixFields: 'Some fields need fixing before this draft can be saved.',
      fieldErrors: {
        required: 'Fill this in.',
        range: 'Use a number from 0 to 5.',
      },
    },
    versions: {
      label: 'v{{version}}',
      approvedOn: 'Approved {{date}}',
      createdOn: 'Started {{date}}',
      basedOn: 'from v{{version}}',
      view: 'View v{{version}}',
      empty: 'No versions yet.',
    },
    bench: {
      hint: 'Run a brief through the concept step side by side. No reel is made; each run is saved against the version it tested.',
      brief: 'Brief',
      briefPlaceholder: 'Paste a brief',
      product: 'Product',
      character: 'Cast',
      noCharacter: 'No one in particular',
      version: 'Playbook version',
      armWithout: 'Without the harness',
      armWith: 'With the harness',
      armWithChecker: 'With the harness and checker',
      model: 'Model',
      models: { haiku: 'Haiku 5.5', sonnet: 'Sonnet 5.5' },
      ideaMode: 'Ideas',
      ideaModes: { explore: 'Explore', iterate: 'Iterate' },
      run: 'Run test',
      runs: 'Saved runs',
      noRuns: 'No test runs yet.',
      runLabel: 'v{{version}} · {{date}}',
      armTitle: '{{label}} · {{model}}',
      seconds: '{{seconds}} s',
      cost: '${{cost}}',
      failed: 'This side failed: {{message}}',
      pending: 'Writing concepts',
      briefTooShort: 'The brief needs at least 10 characters.',
      started: 'Test started. Results appear here as each side finishes.',
      rating: {
        open_one: 'Rate the {{count}} idea',
        open_other: 'Rate all {{count}} ideas',
        older: 'Rate older ideas',
        back: 'Back to test bench',
        progress: 'Idea {{current}} of {{total}}',
        skip: 'Skip',
        score: 'Score',
        low: 'Off brief',
        high: 'As good as the originals',
        saved: 'You rated this {{score}} before.',
        reasonRequired: 'Why not higher?',
        reasonOptional: 'What makes it work? (optional)',
        reasonMissing: 'Say in a sentence why it scores {{score}}.',
        pickScore: 'Pick a score from 1 to 5.',
        save: 'Save and next',
        keys: 'Keys <kbd>1</kbd> to <kbd>5</kbd> score, <kbd>Enter</kbd> saves and moves on, <kbd>S</kbd> skips',
        summaryTitle: 'Rating done',
        summaryCount: 'You rated {{rated}} of {{total}} ideas.',
        ratings: 'Your ratings',
        yourScore: 'You: {{score}}',
        skipped: 'Skipped',
        disagreements: 'Disagreements',
        disagreementsHint: "Ideas where the Critic's score and yours differ by 2 or more.",
        noDisagreements: 'You and the Critic are within 2 points on every idea you rated.',
        criticVsYou: 'Critic {{critic}} · You {{score}}',
      },
    },
    agent: {
      title: 'Harness agent',
      editingDraft: 'Working on the v{{version}} draft',
      readingApproved: 'Reading approved v{{version}}. Keeping a change starts a draft.',
      startOver: 'Start over',
      conversationLabel: 'Conversation with the harness agent',
      you: 'You',
      agentName: 'Agent',
      emptyTitle: 'Ask the agent about this playbook',
      emptyDescription:
        'It reads the playbook, your idea ratings and recent test runs. Every change it suggests waits for you to keep or undo it.',
      emptyReadOnly: 'Nobody has talked to the agent about this playbook yet.',
      suggestionsLabel: 'Try asking',
      suggestions: {
        dramatic: 'Make the transition into the sad world more dramatic',
        lowScore: 'Why did my last test score low?',
        disagreements: 'Where do the Critic and I disagree most?',
      },
      inputLabel: 'Message the harness agent',
      placeholder: 'Tell the agent what to change, or ask it why',
      send: 'Send',
      thinking: 'The agent is thinking. This can take up to a minute.',
      sendFailed: 'The agent could not answer, and your message was not saved. Try again.',
      retry: 'Try again',
      loadFailed: 'The conversation could not be loaded.',
      readOnly: 'You can read this conversation but not add to it.',
      change: {
        keep: 'Keep',
        undo: 'Undo',
        kept: 'Kept',
        undone: 'Undone',
        before: 'Before',
        after: 'After',
        nothing: 'Nothing',
        stale: 'This part of the playbook has changed since. Ask the agent again.',
        failed: 'The change could not be applied: {{detail}}',
        saveEditsFirst: 'Save your playbook edits before keeping or undoing a change.',
      },
      look: {
        heading: 'New look: {{name}}',
        open: {
          person: 'View the person still larger',
          place: 'View the place still larger',
          object: 'View the object still larger',
        },
        openNumbered: 'View sample still {{position}} larger',
        imageDirective: 'Pictures',
        videoDirective: 'Motion',
        soundCues: 'Sound',
        neverShow: 'Never show',
        keptNote: "Added to the brand's looks. Approve it there once the stills look right.",
        stale: 'The brand has a look with this name now. Ask the agent for another.',
        failed: 'The look could not be saved: {{detail}}',
      },
      parts: {
        idea: 'Idea',
        coreRule: 'Core rule',
        tone: 'Tone',
        mustHaves: 'Must-haves',
        neverList: 'Never reuse',
        families: 'Metaphor families',
        hardRules: 'Hard rules',
        conceptTiles: 'Concept pictures',
        coreMove: 'Core move',
        passScore: 'Pass mark',
        beat: 'Formula',
        criterion: 'Scorecard',
      },
      fields: {
        role: 'name',
        intent: 'what it does',
        timing: 'timing',
        look: 'look',
        transitionIn: 'joined by',
        kind: 'written by',
        shot: 'shot',
        product: 'product',
        carriesMetaphorFrom: 'carries the metaphor of',
        demoSource: 'demo footage',
        name: 'name',
        weight: 'weight',
        floor: 'floor',
        description: 'description',
        low: 'an idea that scores 1',
        high: 'an idea that scores 5',
      },
    },
    fit: {
      passed: 'Fits the playbook',
      rewritten: 'Rewritten to fit',
      flagged: 'Flagged',
      unchecked: 'Not checked',
      score: '{{score}} / 5',
      version: 'Playbook v{{version}}',
    },
    learning: {
      proposed_one: '{{count}} new learning proposed',
      proposed_other: '{{count}} new learnings proposed',
      title: 'Proposed learnings',
      onDraft:
        'The Learner wrote these to the v{{version}} draft. Keep what is right and undo the rest; approving the draft puts them to use.',
      noDraft: 'The draft these went to has been approved since.',
      done: 'Done',
      kinds: { added: 'New rule', changed: 'Changed rule', retired: 'Retired rule' },
      evidence: 'Based on these rated ideas',
      untitled: 'Untitled idea',
      noEvidence: 'From your messages to the agent or the brand documents',
      stale:
        'This rule has changed on the draft since, or the draft was approved. Run the Learner again.',
      start: 'Learn from feedback',
      never: "The Learner has not read this playbook's feedback yet.",
      waiting: 'New feedback arrived. The Learner starts in a moment.',
      running: 'The Learner is reading your ratings, messages and brand documents.',
      last: 'Last learned {{date}}: {{summary}}',
      nothingNew: 'Nothing new to learn.',
      failed: 'The last learning run failed: {{message}}',
    },
    rateQueue: {
      progress: '{{rated}} rated, {{left}} to go',
      agreement_one:
        'The Critic is within 1 point of you on {{within}} of the {{count}} idea you rated that it scored (average gap {{gap}}).',
      agreement_other:
        'The Critic is within 1 point of you on {{within}} of the {{count}} ideas you rated that it scored (average gap {{gap}}).',
      loadFailed: 'The ideas to rate could not be loaded.',
      doneTitle: 'Every idea is rated',
      doneDescription: 'New ideas from reels and the test bench will show up here to rate.',
      skippedTitle: 'You have been through the queue',
      skippedDescription_one: '{{count}} idea you skipped is still unrated.',
      skippedDescription_other: '{{count}} ideas you skipped are still unrated.',
      startOver: 'Go through the skipped ideas',
    },
    explored: {
      summary_one: '{{count}} idea explored, here are the top {{top}}',
      summary_other: '{{count}} ideas explored, here are the top {{top}}',
      listLabel: 'Ideas explored',
      rank: '#{{rank}}',
      unranked: 'Not ranked',
      unscored: 'Not scored',
      developed: 'Developed',
      reused: 'Rated highly before',
      gate: {
        passed: 'Passed the gate',
        rejected: 'Rejected by the gate',
        unchecked: 'Not gated',
      },
    },
  },
} as const;
